import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import '../styles/chat-legacy.css'
import ModalSelfie from "./ChatWidget.jsx"
import Tracking from '@/services/tracking'
import {apiFetch} from '@/services/apiBase'
import {
    BEMOBI_SMART_CHECKOUT_BASE_URL,
    GOOGLE_ADS_ID,
    GOOGLE_ADS_INITIATE_CHECKOUT_LABEL,
    GOOGLE_ADS_PURCHASE_LABEL,
    TRACKING_ENABLED,
} from '@/config/env'

/** ===== Utilidades que existiam no chat.js ===== */
const STORAGE_SESSION = 'session_id_bot'
const STORAGE_TOKEN = 'token_auth_bot'
const STORAGE_TOKEN_BOT = 'token_auth_bot_id'
const DEFAULT_USUARIO_BOT_ID = Number(import.meta.env.VITE_DEFAULT_USUARIO_BOT_ID || import.meta.env.VITE_DEFAULT_BOT_ID)

function generateUUID() {
    let d = new Date().getTime()
    let d2 = (typeof performance !== 'undefined' && performance.now && performance.now() * 1000) || 0
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        let r = Math.random() * 16
        if (d > 0) {
            r = (d + r) % 16 | 0
            d = Math.floor(d / 16)
        } else {
            r = (d2 + r) % 16 | 0
            d2 = Math.floor(d2 / 16)
        }
        return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16)
    })
}

function getDeviceInfo() {
    const ua = navigator.userAgent
    let nomeDispositivo = 'Desktop/Notebook', tipoDispositivo = /Mobi|Android/i.test(ua) ? 'mobile' : 'desktop', navegador = 'Desconhecido'
    if (/Chrome\/(\d+)/i.test(ua) && !/Edg/i.test(ua)) {
        navegador = `Chrome ${ua.match(/Chrome\/(\d+)/i)[1]}`
    } else if (/Safari\/(\d+)/i.test(ua) && !/Chrome/i.test(ua)) {
        navegador = `Safari ${ua.match(/Version\/(\d+(\.\d+)?)/i)?.[1] || 'Desconhecido'}`
    } else if (/Firefox\/(\d+)/i.test(ua)) {
        navegador = `Firefox ${ua.match(/Firefox\/(\d+)/i)[1]}`
    } else if (/Edg\/(\d+)/i.test(ua)) {
        navegador = `Edge ${ua.match(/Edg\/(\d+)/i)[1]}`
    }
    if (/iPhone/i.test(ua)) {
        nomeDispositivo = `iPhone ${ua.match(/iPhone(?:.*?OS (\d+_\d+))?/i)?.[1]?.replace('_', '.') || ''}`
    } else if (/iPad/i.test(ua)) {
        nomeDispositivo = 'iPad'
    } else if (/Android/i.test(ua)) {
        nomeDispositivo = `Android ${ua.match(/Android\s+([\d\.]+)/i)?.[1] || ''}`
    }
    return {nomeDispositivo, tipoDispositivo, navegador}
}

const escapeHtml = (s = '') => s.replace(/[&<>"']/g, (m) => ({'&': '&amp', '<': '&lt', '>': '&gt', '"': '&quot', "'": '&#39'}[m]))

function parseJwtPayload(token) {
    if (!token || typeof token !== 'string') return null
    const parts = token.split('.')
    if (parts.length < 2) return null
    try {
        const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/')
        const padded = payload + '='.repeat((4 - (payload.length % 4)) % 4)
        const json = atob(padded)
        return JSON.parse(json)
    } catch (_) {
        return null
    }
}

function isTokenExpired(token, skewMs = 30000) {
    const payload = parseJwtPayload(token)
    if (!payload || !payload.exp) return true
    const expMs = payload.exp * 1000
    return Date.now() + skewMs >= expMs
}

/** ===== Serviço do Bot (portado do chat.js) ===== */
export function useBot(onResponse, {usuarioBotId = DEFAULT_USUARIO_BOT_ID} = {}) {
    const tokenRef = useRef(null)
    const sessionRef = useRef(null)

    // -------- TOKEN --------
    const getToken = useCallback(async () => {
        const cached = localStorage.getItem(STORAGE_TOKEN)
        const cachedBot = localStorage.getItem(STORAGE_TOKEN_BOT)
        if (cached && cachedBot === String(usuarioBotId) && !isTokenExpired(cached)) {
            tokenRef.current = cached
            return cached
        }
        if (cached) {
            localStorage.removeItem(STORAGE_TOKEN)
        }
        if (cachedBot && cachedBot !== String(usuarioBotId)) {
            localStorage.removeItem(STORAGE_TOKEN_BOT)
        }

        const res = await apiFetch(`/sessoes/token?usuarioBotId=${encodeURIComponent(usuarioBotId)}`, {
            method: 'POST', headers: {'Content-Type': 'application/json'}, body: '{}',
        })

        if (!res.ok) throw new Error('Falha ao obter token')

        const {token} = await res.json()
        localStorage.setItem(STORAGE_TOKEN, token)
        localStorage.setItem(STORAGE_TOKEN_BOT, String(usuarioBotId))
        tokenRef.current = token
        return token
    }, [usuarioBotId])

    const refreshToken = useCallback(async () => {
        try {
            localStorage.removeItem(STORAGE_TOKEN)
            localStorage.removeItem(STORAGE_TOKEN_BOT)
            tokenRef.current = null
        } catch {
            // ignora
        }
        return getToken()
    }, [getToken])

    // -------- FETCH COM AUTORIZAÇÃO --------
    const authorizedFetch = useCallback(async (url, init = {}, {retryOn403 = true} = {}) => {
        const baseHeaders = init.headers || {}
        const token = tokenRef.current || (await getToken())
        let headers = {...baseHeaders, Authorization: `Bearer ${token}`}

        let res = await apiFetch(url, {...init, headers})
        if (res.status === 403 && retryOn403) {
            const newToken = await refreshToken()
            headers = {...baseHeaders, Authorization: `Bearer ${newToken}`}
            res = await apiFetch(url, {...init, headers})
        }
        return res
    }, [getToken, refreshToken],)

    // -------- SESSÃO --------
    const ensureSession = useCallback(() => {
        let s = localStorage.getItem(STORAGE_SESSION)
        if (!s) {
            s = generateUUID()
            localStorage.setItem(STORAGE_SESSION, s)
        }
        sessionRef.current = s
        return s
    }, [])

    const registrarPixelGoogle = useCallback(async (pixel) => {
        const sess = sessionRef.current || ensureSession()
        const body = {
            ...pixel,
            provider: 'GOOGLE_ADS',
            sessionId: sess,
            pageUrl: String(pixel?.pageUrl || window.location.href || '').slice(0, 1024),
        }
        const res = await authorizedFetch('/sessoes/pixels', {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify(body),
            keepalive: true,
        }, {retryOn403: true})
        if (!res.ok) throw new Error(`Falha ao registrar pixel (${res.status})`)
        return res.json()
    }, [authorizedFetch, ensureSession])

    // -------- START --------
    const start = useCallback(async () => {
        let sessionId = ensureSession()

        // Pega os parâmetros da URL
        const q = new URLSearchParams(window.location.search);
        const palavraChave = q.get("palavra_chave") || null;
        await getToken()
        const dev = getDeviceInfo();


        const trafego = {
            clienteId: null,
            sessionId,
            telefone: '',
            dataVisualizacao: new Date().toISOString(),
            url: window.location.href,
            nomeCampanha: q.get('nome_campanha'),
            tituloHtml: q.get('nome_html'),
            dataInicioCampanha: q.get('data_inicio_campanha'),
            tituloLandingPage: q.get('data_landing_page'),
            fonteUtm: q.get('fonte_utm'),
            midiaUtm: q.get('midia_utm'),
            termoUtm: q.get('termo_utm'),
            conteudoUtm: q.get('conteudo_utm'),
            nomeDispositivo: dev.nomeDispositivo,
            tipoDispositivo: dev.tipoDispositivo,
            navegador: dev.navegador,
        }

        for (const [key, value] of q.entries()) {
            trafego[key] = value
        }

        try {
            await authorizedFetch(`/sessoes/trafego`, {
                method: 'POST', headers: {'Content-Type': 'application/json'}, body: JSON.stringify(trafego),
            }, {retryOn403: true},)
        } catch {
            // silencioso
        }

        let palavraChaveRetorno = localStorage.getItem("palavra_chave", palavraChave);
        if(palavraChaveRetorno == "null"){
            palavraChaveRetorno = null;
        }
        if(palavraChaveRetorno != palavraChave){
            localStorage.removeItem("session_id_bot");
            localStorage.removeItem(STORAGE_TOKEN)
            localStorage.removeItem(STORAGE_TOKEN_BOT)
            tokenRef.current = null
            localStorage.setItem("palavra_chave", palavraChave);
            sessionId = ensureSession();
        }

        // start
        const customerId = q.get('customerId')
        const startParams = []
        if (palavraChave) {
            startParams.push(`palavra_chave=${encodeURIComponent(palavraChave)}`)
        }
        if (customerId) {
            startParams.push(`customerId=${encodeURIComponent(customerId)}`)
        }
        const startUrl = startParams.length ? `/sessoes/start?${startParams.join('&')}` : '/sessoes/start'
        const res = await authorizedFetch(startUrl, {
            method: 'POST', headers: {'Content-Type': 'application/json'}, body: JSON.stringify({usuarioBotId, sessionId}),
        }, {retryOn403: true},)

        if (!res.ok) throw new Error('Falha ao iniciar sessão')
        const data = await res.json()
        if (data?.sessionId && data.sessionId !== sessionId) {
            try {
                localStorage.setItem(STORAGE_SESSION, data.sessionId)
            } catch (_) {
                // ignora
            }
            sessionRef.current = data.sessionId
            sessionId = data.sessionId
        }
        onResponse?.(data)
    }, [authorizedFetch, ensureSession, getToken, usuarioBotId, onResponse])

    // -------- RESPONDER --------
    const responder = useCallback(async (payload) => {
        const sess = sessionRef.current || ensureSession()
        const res = await authorizedFetch(`/sessoes/${sess}/responder`, {
            method: 'POST', headers: {'Content-Type': 'application/json'}, body: JSON.stringify(payload),
        }, {retryOn403: true},)
        if (!res.ok) throw new Error('Falha ao responder')
        return res.json()
    }, [authorizedFetch, ensureSession],)

    const obterEtapaAtual = useCallback(async () => {
        const sess = sessionRef.current || ensureSession()
        const res = await authorizedFetch(`/sessoes/${sess}/step`, {
            method: 'GET',
        }, {retryOn403: true},)
        if (!res.ok) throw new Error('Falha ao buscar etapa atual')
        return res.json()
    }, [authorizedFetch, ensureSession],)

    const consultarPixStatus = useCallback(async () => {
        const sess = sessionRef.current || ensureSession()
        let res = await authorizedFetch(`/sessoes/${sess}/checkout/status`, {
            method: 'GET',
        }, {retryOn403: true},)
        if (res.status === 404) {
            res = await authorizedFetch(`/sessoes/${sess}/pix/status`, {
                method: 'GET',
            }, {retryOn403: true},)
        }
        if (!res.ok) throw new Error('Falha ao consultar status PIX')
        return res.json()
    }, [authorizedFetch, ensureSession],)

    const gerarCheckoutBemobi = useCallback(async ({paymentMethod, forceRefresh = false} = {}) => {
        const sess = sessionRef.current || ensureSession()
        const body = {}
        if (paymentMethod) {
            body.paymentMethod = paymentMethod
        }
        if (forceRefresh) {
            body.forceRefresh = true
        }
        const res = await authorizedFetch(`/sessoes/${sess}/checkout-session`, {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify(body),
        }, {retryOn403: true},)
        if (!res.ok) throw new Error('Falha ao gerar checkout Bemobi')
        return res.json()
    }, [authorizedFetch, ensureSession],)

    // -------- ENVIO DE ARQUIVOS --------
    const enviarDocumento = useCallback(async (formData) => {
        const token = tokenRef.current || (await getToken())
        const sess = sessionRef.current || ensureSession()
        const res = await apiFetch(`/sessoes/${sess}/documento`, {
            method: 'POST', headers: {Authorization: `Bearer ${token}`}, body: formData,
        })
        if (!res.ok) throw new Error('Falha ao enviar documento')
        return res.json()
    }, [ensureSession, getToken],)

    const enviarSelfie = useCallback(async (formData) => {
        const token = tokenRef.current || (await getToken())
        const sess = sessionRef.current || ensureSession()
        const res = await apiFetch(`/sessoes/${sess}/selfie`, {
            method: 'POST', headers: {Authorization: `Bearer ${token}`}, body: formData,
        })
        if (!res.ok) throw new Error('Falha ao enviar selfie')
        return res.json()
    }, [ensureSession, getToken],)

    // -------- DOWNLOAD DE PDFs --------
    const downloadPDFBase64 = useCallback((base64, fileName = 'arquivo.pdf') => {
        const blob = pdfBase64ToBlob(base64)
        if (!blob) return

        const url = URL.createObjectURL(blob)

        const a = document.createElement('a')
        a.href = url
        a.download = fileName
        a.style.display = 'none'
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)

        setTimeout(() => URL.revokeObjectURL(url), 5000)
    }, [])

    const fetchPDFBlob = useCallback(async (linkPDF) => {
        const token = tokenRef.current || (await getToken())
        const raw = String(linkPDF).replaceAll('//api', '/api')
        const normalized = /^https?:\/\//i.test(raw) ? raw : `/${raw.replace(/^\/+/, '')}`
        const res = await apiFetch(normalized, {
            headers: {Authorization: `Bearer ${token}`},
        })
        if (!res.ok) throw new Error('Erro ao baixar PDF')
        return res.blob()
    }, [getToken],)

    const downloadPDF = useCallback(async (name, linkPDF) => {
        const blob = await fetchPDFBlob(linkPDF)
        downloadBlob(blob, name)
    }, [fetchPDFBlob],)

    // -------- API PÚBLICA --------
    const enviarMsgTexto = useCallback((valor) => responder({tipo: 'TEXT_INPUT', valor}), [responder])
    const enviarPlanoSelecionado = useCallback((chaves) => responder({tipo: 'LIST', chaves}), [responder])
    const enviarButton = useCallback((valor) => responder({tipo: 'BUTTONS', valor}), [responder])

    return useMemo(() => ({
        start, enviarMsgTexto, enviarPlanoSelecionado, enviarButton, enviarDocumento, enviarSelfie, downloadPDFBase64, fetchPDFBlob, downloadPDF, obterEtapaAtual, consultarPixStatus, gerarCheckoutBemobi, registrarPixelGoogle,
    }), [start, enviarMsgTexto, enviarPlanoSelecionado, enviarButton, enviarDocumento, enviarSelfie, downloadPDFBase64, fetchPDFBlob, downloadPDF, obterEtapaAtual, consultarPixStatus, gerarCheckoutBemobi, registrarPixelGoogle],)
}

function downloadBlob(blob, filename) {
    const safeName = buildPdfName(filename)
    let file
    try {
        file = new File([blob], safeName, {type: 'application/pdf'})
    } catch (_) {
        file = blob
    }
    if (window.navigator?.msSaveOrOpenBlob) {
        window.navigator.msSaveOrOpenBlob(file, safeName)
        return
    }
    const url = URL.createObjectURL(file)
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = file.name || safeName
    anchor.type = file.type || 'application/pdf'
    anchor.rel = 'noopener'
    anchor.style.display = 'none'
    document.body.appendChild(anchor)
    anchor.dispatchEvent(new MouseEvent('click', {view: window, bubbles: true, cancelable: true}))
    requestAnimationFrame(() => {
        if (anchor.parentNode) anchor.parentNode.removeChild(anchor)
    })
    setTimeout(() => URL.revokeObjectURL(url), 3000)
}

function buildPdfName(name) {
    const fallback = 'arquivo.pdf'
    const raw = (name || fallback).toString().trim() || fallback
    const sanitized = raw.replace(/[<>:"/\\|?*\u0000-\u001F]/g, '_')
    return sanitized.toLowerCase().endsWith('.pdf') ? sanitized : `${sanitized}.pdf`
}

function pdfBase64ToBlob(base64) {
    if (!base64) return null
    const clean = base64.replace(/^data:application\/pdfbase64,/, '').replace(/[\n\r\s]/g, '')
    if (!clean) return null
    const binary = atob(clean)
    const len = binary.length
    const bytes = new Uint8Array(len)
    for (let i = 0; i < len; i += 1) {
        bytes[i] = binary.charCodeAt(i)
    }
    return new Blob([bytes], {type: 'application/pdf'})
}

/** ===== Componente do Chat com toda a UI e handlers ===== */
export default function LegacyChat() {
    const chatRef = useRef(null)
    const messagesRef = useRef(null)
    const textareaRef = useRef(null)
    const sendBtnRef = useRef(null)
    const pixCodeRef = useRef('')
    const bemobiCheckoutRef = useRef({
        key: '',
        refreshKey: '',
        refreshInFlight: false,
        refreshRequestedAt: {},
        missingUrlRetryAttempts: {},
        missingUrlRetryTimers: {},
        checkoutUrl: '',
        sessionId: '',
        paymentMethod: '',
        checkoutButtons: [],
        renewTimeoutId: null,
        renewInFlight: false,
        renewAttempts: 0,
    })
    const lastClipboardTextRef = useRef('')
    const trafficPixelsRef = useRef([])
    const purchaseContextRef = useRef({
        value: null,
        currency: 'BRL',
        productId: null,
        prazo: null,
        planName: null,
    })
    const firedBotEventsRef = useRef(new Set())
    const pendingBotPlaceholdersRef = useRef([])
    const activeBotPlaceholderRef = useRef(null)
    const [showWebcam, setShowWebcam] = React.useState(false)
    const [actionAtual, setActionAtual] = React.useState()
    const [fazendoUploadArquivo, setFazendoUploadArquivo] = React.useState(false)
    const [disableEnviar, setDisableEnviar] = React.useState(false)

    const [previewImage, setPreviewImage] = useState(null)

    const bot = useBot(handleResponse)
    const closePreviewImage = useCallback(() => setPreviewImage(null), [])

    const [sending, setSending] = React.useState(false)
    const envioBloqueadoRef = useRef(false)
    const PIX_POLL_MAX_ATTEMPTS = 120
    const PIX_POLL_MAX_DURATION_MS = 10 * 60 * 1000
    const BEMOBI_RENEW_AFTER_MS = 9 * 60 * 1000
    const BEMOBI_RENEW_MAX_ATTEMPTS = 3
    const BEMOBI_MISSING_URL_RETRY_MAX_ATTEMPTS = 8
    const BEMOBI_MISSING_URL_RETRY_BASE_DELAY_MS = 1000
    const pixPollingRef = useRef({
        active: false,
        key: null,
        attempts: 0,
        startedAt: 0,
        timeoutId: null,
        syncing: false,
    })

    useEffect(() => {
        envioBloqueadoRef.current = sending || fazendoUploadArquivo || disableEnviar
    }, [sending, fazendoUploadArquivo, disableEnviar])

    useEffect(() => {
        return () => {
            const state = bemobiCheckoutRef.current
            if (state?.renewTimeoutId) {
                clearTimeout(state.renewTimeoutId)
            }
            if (state?.missingUrlRetryTimers) {
                Object.values(state.missingUrlRetryTimers).forEach((timerId) => {
                    if (timerId) clearTimeout(timerId)
                })
                state.missingUrlRetryTimers = {}
            }
        }
    }, [])

    const stopPixPolling = useCallback(() => {
        const state = pixPollingRef.current
        if (state?.timeoutId) {
            clearTimeout(state.timeoutId)
        }
        pixPollingRef.current = {
            active: false,
            key: null,
            attempts: 0,
            startedAt: 0,
            timeoutId: null,
            syncing: false,
        }
    }, [])

    const schedulePixPolling = useCallback((delayMs = 3500) => {
        const state = pixPollingRef.current
        if (!state.active) return

        state.timeoutId = setTimeout(async () => {
            const currentState = pixPollingRef.current
            if (!currentState.active) return

            let status = null
            try {
                status = await bot.consultarPixStatus()
            } catch (error) {
                console.debug('Falha ao consultar status PIX automaticamente', error)
            }

            const afterRequestState = pixPollingRef.current
            if (!afterRequestState.active || afterRequestState.key !== currentState.key) return

            const paid = Boolean(status?.paid)
            const advanced = Boolean(status?.advanced)
            const stillRequiresPayment = Boolean(
                status?.requiresPayment ??
                status?.requiresPix ??
                status?.requiresBemobiCheckout
            )

            if (paid && (advanced || !stillRequiresPayment)) {
                if (afterRequestState.syncing) return
                afterRequestState.syncing = true
                stopPixPolling()
                try {
                    const nextStep = await bot.obterEtapaAtual()
                    if (nextStep) {
                        enqueueBotPlaceholder()
                        handleResponse(nextStep)
                    }
                } catch (error) {
                    console.error('Falha ao sincronizar etapa apos pagamento PIX', error)
                }
                return
            }

            afterRequestState.attempts += 1
            const elapsed = Date.now() - afterRequestState.startedAt
            if (afterRequestState.attempts >= PIX_POLL_MAX_ATTEMPTS || elapsed >= PIX_POLL_MAX_DURATION_MS) {
                stopPixPolling()
                return
            }

            const nextDelay = afterRequestState.attempts < 8 ? 3500 : (afterRequestState.attempts < 30 ? 5000 : 8000)
            schedulePixPolling(nextDelay)
        }, delayMs)
    }, [bot, stopPixPolling])

    const maybeStartPixPolling = useCallback((payload) => {
        const requiresPix = Boolean(payload?.props?.requiresPix)
        const requiresBemobiCheckout = Boolean(payload?.props?.requiresBemobiCheckout)
        const requiresPayment = requiresPix || requiresBemobiCheckout
        const orderId = payload?.contexto?.pix?.orderId ||
            payload?.contexto?.bemobiCheckout?.erpInvoiceId ||
            payload?.bemobiCheckout?.erpInvoiceId ||
            payload?.contexto?.bemobiCheckout?.sessionId ||
            payload?.bemobiCheckout?.sessionId
        const sessionId = payload?.sessionId || localStorage.getItem(STORAGE_SESSION) || ''

        if (!requiresPayment || !orderId || !sessionId) {
            stopPixPolling()
            return
        }

        const key = `${sessionId}:${payload?.ordem ?? ''}:${orderId}`
        const state = pixPollingRef.current
        if (state.active && state.key === key) {
            return
        }

        stopPixPolling()
        pixPollingRef.current = {
            active: true,
            key,
            attempts: 0,
            startedAt: Date.now(),
            timeoutId: null,
            syncing: false,
        }
        schedulePixPolling(3000)
    }, [schedulePixPolling, stopPixPolling])


    useEffect(() => {
        bot.start().catch((e) => console.error(e))

        const btn = sendBtnRef.current
        const txt = textareaRef.current
        const messagesEl = messagesRef.current
        const onKey = (e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                if (envioBloqueadoRef.current) return
                send()
            }
        }
        btn?.addEventListener('click', send)
        txt?.addEventListener('keydown', onKey)

        document.addEventListener('click', openClickDelegate, true)
        chatRef.current
            ?.querySelector('#close-chat')
            ?.addEventListener('click', closeChat)
        chatRef.current
            ?.querySelector('#reset-chat')
            ?.addEventListener('click', resetChat)

        let observer
        let resizeObserver
        let loadHandler
        if (messagesEl && typeof MutationObserver !== 'undefined') {
            observer = new MutationObserver(() => {
                scheduleScrollToEnd()
            })
            observer.observe(messagesEl, {childList: true, subtree: true})
        }
        if (messagesEl && typeof ResizeObserver !== 'undefined') {
            resizeObserver = new ResizeObserver(() => {
                scheduleScrollToEnd()
            })
            resizeObserver.observe(messagesEl)
        }
        if (messagesEl) {
            loadHandler = (event) => {
                const target = event.target
                if (!target) return
                if (target.tagName === 'IMG' || target.tagName === 'IFRAME' || target.tagName === 'VIDEO') {
                    scheduleScrollToEnd()
                }
            }
            messagesEl.addEventListener('load', loadHandler, true)
            messagesEl.addEventListener('loadedmetadata', loadHandler, true)
            messagesEl.addEventListener('loadeddata', loadHandler, true)
        }

        window.openChat = openChat
        window.closeChatWidget = closeChat
        const legacyChatApi = {open: openChat, close: closeChat, testPdfDownload: downloadPdfInBackground}
        window.LegacyChat = legacyChatApi

        if (TRACKING_ENABLED) {
            rewriteWhatsappLinks()
        }
        hideInitialLoading()

        const isMobileViewport = typeof window.matchMedia === 'function'
            ? window.matchMedia('(max-width: 766px)').matches
            : window.innerWidth <= 766
        const isMobileByUserAgent = /Mobi|Android|iPhone|iPad|iPod/i.test(window.navigator?.userAgent || '')
        const shouldUseMobileAutoOpenDelay = isMobileViewport || isMobileByUserAgent
        const autoOpenDelayMs = shouldUseMobileAutoOpenDelay ? 2000 : 0
        let autoOpenTimerId = null

        const focusChatInput = () => {
            if (window.innerWidth > 766) {
                setTimeout(() => textareaRef.current?.focus(), 250)
            }
        }

        function triggerAutoOpenChat() {
            openChat()
            focusChatInput()
        }

        function cleanupAutoOpenTimer() {
            if (autoOpenTimerId) {
                clearTimeout(autoOpenTimerId)
                autoOpenTimerId = null
            }
        }

        if (autoOpenDelayMs > 0) {
            autoOpenTimerId = setTimeout(triggerAutoOpenChat, autoOpenDelayMs)
        } else {
            triggerAutoOpenChat()
        }

        return () => {
            btn?.removeEventListener('click', send)
            txt?.removeEventListener('keydown', onKey)
            document.removeEventListener('click', openClickDelegate, true)
            chatRef.current
                ?.querySelector('#close-chat')
                ?.removeEventListener('click', closeChat)
            chatRef.current
                ?.querySelector('#reset-chat')
                ?.removeEventListener('click', resetChat)
            observer?.disconnect()
            resizeObserver?.disconnect()
            if (messagesEl && loadHandler) {
                messagesEl.removeEventListener('load', loadHandler, true)
                messagesEl.removeEventListener('loadedmetadata', loadHandler, true)
                messagesEl.removeEventListener('loadeddata', loadHandler, true)
            }
            if (window.openChat === openChat) delete window.openChat
            if (window.closeChatWidget === closeChat) delete window.closeChatWidget
            if (window.LegacyChat === legacyChatApi) delete window.LegacyChat
            cleanupAutoOpenTimer()
            stopPixPolling()
        }
        // eslint-disable-next-line
    }, [])

    useEffect(() => {
        if (!previewImage) return

        const onKeyDown = (event) => {
            if (event.key === 'Escape') {
                closePreviewImage()
            }
        }

        const previousOverflow = document.body.style.overflow
        document.body.style.overflow = 'hidden'
        window.addEventListener('keydown', onKeyDown)

        return () => {
            window.removeEventListener('keydown', onKeyDown)
            document.body.style.overflow = previousOverflow
        }
    }, [previewImage, closePreviewImage])

    useEffect(() => {
        const list = messagesRef.current
        if (!list) return

        const onMessageImageClick = (event) => {
            const target = event.target
            if (!(target instanceof Element)) return

            const clicked = target.closest('li[data-html-msg] img')
            if (!(clicked instanceof HTMLImageElement)) return

            event.preventDefault()
            setPreviewImage({
                src: clicked.currentSrc || clicked.src,
                alt: clicked.alt || 'Imagem da mensagem',
            })
        }

        list.addEventListener('click', onMessageImageClick)
        return () => list.removeEventListener('click', onMessageImageClick)
    }, [])

    function hideInitialLoading() {
        const el = chatRef.current?.querySelector('#loading-chat')
        if (el) {
            el.style.display = 'none'
            el.classList.remove('d-flex')
        }
    }

    function rewriteWhatsappLinks() {
        const qa = new URLSearchParams(window.location.search)
        const dev = getDeviceInfo()

        let args = {
            clienteId: null,
            sessionId: localStorage.getItem(STORAGE_SESSION),
            telefone: '',
            dataVisualizacao: new Date().toISOString(),
            url: window.location.href,
            nomeCampanha: qa.get('nome_campanha'),
            tituloHtml: qa.get('nome_html'),
            dataInicioCampanha: qa.get('data_inicio_campanha'),
            tituloLandingPage: qa.get('data_landing_page'),
            fonteUtm: qa.get('fonte_utm'),
            midiaUtm: qa.get('midia_utm'),
            termoUtm: qa.get('termo_utm'),
            conteudoUtm: qa.get('conteudo_utm'),
            nomeDispositivo: dev.nomeDispositivo,
            tipoDispositivo: dev.tipoDispositivo,
            navegador: dev.navegador,
        }

        // Converte args em query string
        const argsQuery = new URLSearchParams(args).toString()

        // Mantém os parâmetros originais também
        const originalQuery = window.location.search.replace(/^\?/, '')

        let finalQuery = argsQuery
        if (originalQuery) {
            finalQuery += `&${originalQuery}`
        }

        const target = `https://ksk.syncrono.com.br/api/events/trafego?${finalQuery}`

        document.querySelectorAll('.whatsapp-btn').forEach((a) => {
            if (a.getAttribute('href') !== '#') {
                a.setAttribute('href', target)
            }
            a._trafficUrlTarget = target
        })
        document.querySelectorAll('.bot-button, .floating-btn').forEach((element) => {
            if (!element) return
            element._trafficUrlTarget = target
            if (element.tagName === 'A') {
                element.setAttribute('href', '#')
                element.setAttribute('role', 'button')
                element.removeAttribute('target')
            }
            if (element.tagName === 'BUTTON' && !element.hasAttribute('type')) {
                element.setAttribute('type', 'button')
            }
        })
    }

    function handleTrafficPixel(element) {
        if (!element) return
        const trafficUrl = element._trafficUrlTarget || element.getAttribute('data-traffic-url')
        if (trafficUrl) {
            fireTrafficPixel(trafficUrl)
        }
    }

    function fireTrafficPixel(url) {
        if (!url) return
        try {
            const separator = url.includes('?') ? '&' : '?'
            const targetedUrl = `${url}${separator}_ts=${Date.now()}`
            const pixel = new Image(1, 1)
            pixel.src = targetedUrl
            trafficPixelsRef.current = [...trafficPixelsRef.current, pixel]
            setTimeout(() => {
                trafficPixelsRef.current = trafficPixelsRef.current.filter((item) => item !== pixel)
            }, 10000)
        } catch (err) {
            console.debug('Erro ao enviar pixel de tráfego', err)
        }
    }

    function normalizeText(value) {
        if (value === null || value === undefined) return null
        const text = String(value).trim()
        return text ? text : null
    }

    function parseCurrencyValue(value) {
        if (value === null || value === undefined) return null
        if (typeof value === 'number') {
            return Number.isFinite(value) ? value : null
        }
        const text = String(value).trim()
        if (!text) return null
        const cleaned = text.replace(/[^\d.,-]/g, '')
        if (!cleaned) return null
        const hasComma = cleaned.includes(',')
        const hasDot = cleaned.includes('.')
        let normalized = cleaned
        if (hasComma && hasDot) {
            normalized = cleaned.replace(/\./g, '').replace(',', '.')
        } else if (hasComma && !hasDot) {
            normalized = cleaned.replace(',', '.')
        }
        const parsed = Number.parseFloat(normalized)
        return Number.isFinite(parsed) ? parsed : null
    }

    function findValueByKeys(target, patterns, visited = new WeakSet()) {
        if (!target || typeof target !== 'object') return null
        if (visited.has(target)) return null
        visited.add(target)

        if (Array.isArray(target)) {
            for (const item of target) {
                const found = findValueByKeys(item, patterns, visited)
                if (found !== null && found !== undefined) return found
            }
            return null
        }

        for (const [key, value] of Object.entries(target)) {
            const normalizedKey = String(key).toLowerCase()
            const matches = patterns.some((pattern) => {
                return typeof pattern === 'string' ? normalizedKey === pattern : pattern.test(normalizedKey)
            })
            if (matches) {
                if (value !== null && value !== undefined && typeof value !== 'object') {
                    return value
                }
                const nested = findValueByKeys(value, patterns, visited)
                if (nested !== null && nested !== undefined) return nested
            }
        }

        for (const value of Object.values(target)) {
            const found = findValueByKeys(value, patterns, visited)
            if (found !== null && found !== undefined) return found
        }
        return null
    }

    function findInPayload(payload, patterns) {
        const roots = [payload?.props, payload?.contexto, payload?.items, payload]
        for (const root of roots) {
            const found = findValueByKeys(root, patterns)
            if (found !== null && found !== undefined) {
                return found
            }
        }
        return null
    }

    function updatePurchaseContext(payload) {
        if (!payload) return
        const planIdKeys = ['produto_id', 'product_id', 'plano_id', 'plan_id', 'codigo_plano', /produto.*id/i, /plano.*id/i, /plan.*id/i, /plan.*code/i, /sku/i,]
        const planNameKeys = ['plano', 'plan_name', 'nome_plano', 'plano_nome', /plan.*name/i, /nome.*plano/i,]
        const prazoKeys = ['prazo', 'prazo_meses', 'prazo_mes', /prazo/i, /meses/i]

        let productId = normalizeText(findInPayload(payload, planIdKeys))
        const planName = normalizeText(findInPayload(payload, planNameKeys))
        const prazo = normalizeText(findInPayload(payload, prazoKeys))

        if (!productId && planName) {
            productId = planName
        }
        if (productId) purchaseContextRef.current.productId = productId
        if (planName) purchaseContextRef.current.planName = planName
        if (prazo) purchaseContextRef.current.prazo = prazo

        const valueKeys = ['valor_contrato', 'valorContrato', 'valor_total', 'valorTotal', 'total', 'preco', 'price', 'amount', 'value', /valor/i,]
        const currencyKeys = ['currency', 'moeda', 'currency_code', 'codigo_moeda']
        const rawValue = findInPayload(payload, valueKeys)
        const parsedValue = parseCurrencyValue(rawValue)
        if (parsedValue !== null) {
            purchaseContextRef.current.value = parsedValue
        }
        const currency = normalizeText(findInPayload(payload, currencyKeys))
        if (currency) {
            purchaseContextRef.current.currency = currency.toUpperCase()
        }
    }

    function extractTrackingEvents(payload) {
        const events = new Map()
        const raw = payload?.props?.trackingEvents || payload?.contexto?.trackingEvents
        if (Array.isArray(raw)) {
            raw.forEach((e) => {
                if (e === null || e === undefined) return
                if (typeof e === 'string') {
                    const description = e.trim()
                    if (description) events.set(`description:${description}`, {id: '', description})
                    return
                }
                if (typeof e === 'object') {
                    const id = (e.id || e.eventId || e.eventoId || e.codigo || '').toString().trim()
                    const description = (e.descricao || e.description || e.event || '').toString().trim()
                    if (id || description) events.set(id || `description:${description}`, {id, description})
                    return
                }
                const description = String(e).trim()
                if (description) events.set(`description:${description}`, {id: '', description})
            })
        }
        return Array.from(events.values())
    }

    function normalizeTrackingDescription(value) {
        return String(value || '')
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .trim()
            .toLowerCase()
    }

    function resolveGoogleSourceEvent(event) {
        const id = String(event?.id || '').trim()
        if (id === '16' || id === '25') return id
        const description = normalizeTrackingDescription(event?.description)
        if (description === 'solicitacao de telefone') return '16'
        if (description === 'disponibilizacao do pix e boleto') return '25'
        return ''
    }

    function pixelDispatchStatus(triggered) {
        if (!TRACKING_ENABLED || !GOOGLE_ADS_ID) return 'DISABLED'
        return triggered ? 'TRIGGERED' : 'ERROR'
    }

    function persistGooglePixel(pixel) {
        bot.registrarPixelGoogle(pixel).catch((error) => {
            console.debug('Falha ao salvar pixel Google; nova tentativa sera feita', error)
            window.setTimeout(() => {
                bot.registrarPixelGoogle(pixel).catch((retryError) => {
                    console.debug('Falha ao salvar pixel Google apos nova tentativa', retryError)
                })
            }, 1500)
        })
    }

    function handleBotEventFromResponse(payload) {
        updatePurchaseContext(payload)
        const events = extractTrackingEvents(payload)
        if (!events.length) return
        for (const event of events) {
            const sourceEventId = resolveGoogleSourceEvent(event)
            const eventKey = sourceEventId || event.id || event.description
            if (firedBotEventsRef.current.has(eventKey)) continue

            if (sourceEventId === '16') {
                const triggered = !!Tracking?.trackInitiateCheckout?.()
                persistGooglePixel({
                    pixelId: GOOGLE_ADS_ID || 'NOT_CONFIGURED',
                    conversionLabel: GOOGLE_ADS_INITIATE_CHECKOUT_LABEL,
                    eventName: 'INITIATE_CHECKOUT',
                    sourceEventId,
                    dispatchStatus: pixelDispatchStatus(triggered),
                })
                firedBotEventsRef.current.add(eventKey)
                continue
            }

            if (sourceEventId === '25') {
                const sessionId = localStorage.getItem(STORAGE_SESSION) || payload?.sessionId || ''
                const context = purchaseContextRef.current || {}
                const purchase = {
                    value: typeof context.value === 'number' ? context.value : undefined,
                    currency: context.currency || 'BRL',
                    transactionId: sessionId || undefined,
                    productId: context.productId || undefined,
                    prazo: context.prazo || undefined,
                    planName: context.planName || undefined,
                }
                const triggered = !!Tracking?.trackPurchase?.(purchase)
                persistGooglePixel({
                    pixelId: GOOGLE_ADS_ID || 'NOT_CONFIGURED',
                    conversionLabel: GOOGLE_ADS_PURCHASE_LABEL,
                    eventName: 'PURCHASE',
                    sourceEventId,
                    dispatchStatus: pixelDispatchStatus(triggered),
                    ...purchase,
                })
                firedBotEventsRef.current.add(eventKey)
                continue
            }

            if (Tracking?.handleBotEvent?.(event.description || event.id, payload)) {
                firedBotEventsRef.current.add(eventKey)
            }
        }
    }

    function openClickDelegate(e) {
        const t = e.target.closest?.('.whatsapp-btn, .floating-btn, .bot-button, .pop-up-mobile, .close-btn, [data-open-chat]')
        if (!t) return
        if (t.classList.contains('close-btn')) {
            document.getElementById('popup')?.style && (document.getElementById('popup').style.display = 'none')
            return
        }
        if (t.matches('[data-open-chat], .bot-button, .floating-btn, .whatsapp-btn')) {
            e.preventDefault()
            if (TRACKING_ENABLED) {
                handleTrafficPixel(t)
            }
        }
        openChat()
        setTimeout(() => textareaRef.current?.focus(), 250)
    }

    function openChat() {
        const el = chatRef.current
        if (!el) return
        if (window.innerWidth < 500) {
            document.documentElement.style.setProperty('overflow', 'hidden', 'important')
            document.body.style.setProperty('overflow', 'hidden', 'important')
        }
        el.style.right = '0px'
    }

    function closeChat() {
        const el = chatRef.current
        if (!el) return
        if (window.innerWidth < 500) {
            document.documentElement.style.removeProperty('overflow')
            document.body.style.removeProperty('overflow')
        }
        el.style.right = '-550px'
    }

    async function resetChat() {
        try {
            localStorage.removeItem(STORAGE_SESSION)
            localStorage.removeItem(STORAGE_TOKEN)
            localStorage.removeItem(STORAGE_TOKEN_BOT)
            if ('caches' in window) {
                const keys = await caches.keys()
                await Promise.all(keys.map((k) => caches.delete(k)))
            }
            if ('serviceWorker' in navigator) {
                const regs = (await navigator.getRegistrations?.()) || (await navigator.serviceWorker.getRegistrations())
                await Promise.all(regs.map((r) => r.unregister()))
            }
            const url = new URL(window.location.href)
            url.searchParams.delete('session_id')
            url.searchParams.set('chat_reset', Date.now().toString())
            window.location.replace(url.toString())
        } catch (e) {
            console.error(e)
        }
    }

    async function send() {
        if (envioBloqueadoRef.current) return
        const txt = textareaRef.current
        const val = (txt?.value || '').trim()
        if (!val) return
        envioBloqueadoRef.current = true
        appendMe(val)
        txt.value = ''
        scrollToEnd()
        try {
            setSending(true)
            enqueueBotPlaceholder()
            const resp = await bot.enviarMsgTexto(val)
            handleResponse(resp)
        } catch (e) {
            consumeBotPlaceholder()
            console.error('Erro ao enviar', e)
        } finally {
            setSending(false)
            envioBloqueadoRef.current = false
            if (!disableEnviar && !fazendoUploadArquivo) {
                requestAnimationFrame(() => textareaRef.current?.focus())
            }
        }
    }

    /** ===== Renderização das respostas do backend ===== */
    function hasVisualMediaMarkup(html) {
        if (!html || typeof html !== 'string') return false
        return /<(img|video|iframe)\b/i.test(html)
    }

    function isApprovalHighlightMarkup(html) {
        if (!html || typeof html !== 'string') return false
        const normalized = html.toLowerCase()
        return normalized.includes('tim-aprovacao-credito') || /limite de cr[eé]dito.*aprovado/.test(normalized)
    }

    function isDiscountHighlightMarkup(html) {
        if (!html || typeof html !== 'string') return false
        const normalized = html.toLowerCase()
        return normalized.includes('tim-bonus-checkout') || normalized.includes('bônus especial') || normalized.includes('bonus especial') || /\b\d{1,2}% off\b/.test(normalized)
    }

    function hasSecondaryPayload(data) {
        return (data?.props?.attachments?.length > 0) ||
            (data?.contexto?.contratoPdfB64 && data?.props?.autoGenerateContract) ||
            (Array.isArray(data?.props?.buttons) && data.props.buttons.length > 0) ||
            (data?.items && data?.items?.bensCredito)
    }

    function asText(value) {
        if (value == null) return ''
        return String(value).trim()
    }

    function normalizeBemobiPaymentMethod(value) {
        const normalized = asText(value).toUpperCase()
        if (!normalized) return ''
        if (normalized.includes('PIX')) return 'PIX'
        if (normalized.includes('CART') || normalized.includes('CREDIT')) return 'CREDIT_CARD'
        return normalized
    }

    function resolveBemobiCheckoutPayload(payload) {
        const contexto = payload?.contexto
        if (contexto && typeof contexto === 'object' && contexto.bemobiCheckout && typeof contexto.bemobiCheckout === 'object') {
            return contexto.bemobiCheckout
        }
        if (payload?.bemobiCheckout && typeof payload.bemobiCheckout === 'object') {
            return payload.bemobiCheckout
        }
        return null
    }

    function resolveBemobiCheckoutUrl(checkout) {
        if (!checkout || typeof checkout !== 'object') return ''
        const directUrl = asText(checkout.checkoutUrl || checkout.url || checkout.paymentUrl)
        if (directUrl) {
            return ensureBemobiRawParam(directUrl)
        }
        const sessionCode = asText(checkout.sessionId || checkout.checkoutSessionId)
        return buildBemobiSmartCheckoutUrl(sessionCode)
    }

    function ensureBemobiRawParam(url) {
        const value = asText(url)
        if (!value) return ''

        const lower = value.toLowerCase()
        const isBemobiSmartCheckout =
            lower.includes('smart-checkout.bemobi.com/checkout') ||
            lower.includes('/embed/invoice')
        if (!isBemobiSmartCheckout) {
            return value
        }

        const hashIndex = value.indexOf('#')
        const hash = hashIndex >= 0 ? value.slice(hashIndex) : ''
        const baseWithQuery = hashIndex >= 0 ? value.slice(0, hashIndex) : value
        const queryIndex = baseWithQuery.indexOf('?')

        if (queryIndex < 0) {
            return `${baseWithQuery}?raw=true${hash}`
        }

        const base = baseWithQuery.slice(0, queryIndex)
        const query = baseWithQuery.slice(queryIndex + 1)
        const tokens = query
            .split('&')
            .filter((part) => part && !part.toLowerCase().startsWith('raw='))
        tokens.push('raw=true')
        return `${base}?${tokens.join('&')}${hash}`
    }

    function resolveBemobiSessionId(checkout) {
        if (!checkout || typeof checkout !== 'object') return ''
        return asText(checkout.sessionId || checkout.checkoutSessionId)
    }

    function buildBemobiSmartCheckoutUrl(sessionCode) {
        if (!sessionCode) return ''
        let baseUrl = asText(BEMOBI_SMART_CHECKOUT_BASE_URL) || 'https://smart-checkout.bemobi.com/checkout'
        while (baseUrl.endsWith('/')) {
            baseUrl = baseUrl.slice(0, -1)
        }
        let checkoutUrl = ''
        if (baseUrl.includes('?')) {
            checkoutUrl = `${baseUrl}&code=${encodeURIComponent(sessionCode)}`
        } else {
            checkoutUrl = `${baseUrl}?code=${encodeURIComponent(sessionCode)}`
        }
        return ensureBemobiRawParam(checkoutUrl)
    }

    function cacheBemobiCheckout(payload) {
        const checkout = resolveBemobiCheckoutPayload(payload)
        if (!checkout) return

        const checkoutUrl = resolveBemobiCheckoutUrl(checkout)
        const sessionId = resolveBemobiSessionId(checkout)
        const paymentMethod = normalizeBemobiPaymentMethod(payload?.props?.bemobiPaymentMethod || checkout?.paymentMethod || bemobiCheckoutRef.current.paymentMethod)

        if (checkoutUrl) {
            bemobiCheckoutRef.current.checkoutUrl = checkoutUrl
        }
        if (sessionId) {
            bemobiCheckoutRef.current.sessionId = sessionId
        }
        if (paymentMethod) {
            bemobiCheckoutRef.current.paymentMethod = paymentMethod
        }
    }

    async function copyToClipboard(text) {
        const value = asText(text)
        if (!value) return false

        if (navigator?.clipboard?.writeText && window.isSecureContext) {
            try {
                await navigator.clipboard.writeText(value)
                return true
            } catch {
                // fallback abaixo
            }
        }

        try {
            const textArea = document.createElement('textarea')
            textArea.value = value
            textArea.setAttribute('readonly', '')
            textArea.style.position = 'fixed'
            textArea.style.left = '-9999px'
            textArea.style.opacity = '0'
            document.body.appendChild(textArea)
            textArea.focus()
            textArea.select()
            textArea.setSelectionRange(0, value.length)
            const copied = document.execCommand('copy')
            document.body.removeChild(textArea)
            return copied
        } catch {
            return false
        }
    }

    function buildBemobiButtonsKey(buttons) {
        if (!Array.isArray(buttons) || !buttons.length) return ''
        return buttons
            .map((btn) => `${normalizeActionText(btn?.action)}:${asText(btn?.label)}`)
            .filter(Boolean)
            .join('|')
    }

    function resolveBemobiCheckoutButtons(payload) {
        const fromPayload = Array.isArray(payload?.props?.buttons)
            ? payload.props.buttons.filter((btn) => asText(btn?.action))
            : []
        if (fromPayload.length) {
            bemobiCheckoutRef.current.checkoutButtons = fromPayload
            return fromPayload
        }
        return Array.isArray(bemobiCheckoutRef.current.checkoutButtons)
            ? bemobiCheckoutRef.current.checkoutButtons.filter((btn) => asText(btn?.action))
            : []
    }

    function buildBemobiRenderKey(payload, checkoutUrl, sessionId, paymentMethod, actionButtons) {
        const localSessionId = asText(payload?.sessionId || localStorage.getItem(STORAGE_SESSION))
        return [localSessionId, sessionId, checkoutUrl, paymentMethod, buildBemobiButtonsKey(actionButtons)].join('|')
    }

    function appendBemobiCheckoutIframe(payload) {
        if (!payload?.props?.requiresBemobiCheckout) return

        const checkout = resolveBemobiCheckoutPayload(payload)
        const checkoutUrl = resolveBemobiCheckoutUrl(checkout)
        const sessionId = resolveBemobiSessionId(checkout)
        const paymentMethod = normalizeBemobiPaymentMethod(payload?.props?.bemobiPaymentMethod || checkout?.paymentMethod || bemobiCheckoutRef.current.paymentMethod)
        const actionButtons = resolveBemobiCheckoutButtons(payload)
        const urlRedirect = asText(payload?.props?.url_redirect)

        if (!checkoutUrl) {
            triggerBemobiMissingUrlRetry(paymentMethod || bemobiCheckoutRef.current.paymentMethod || 'CREDIT_CARD', 'missing-checkout-url')
            return
        }
        clearBemobiMissingUrlRetry(paymentMethod || bemobiCheckoutRef.current.paymentMethod || 'CREDIT_CARD')

        const renderKey = buildBemobiRenderKey(payload, checkoutUrl, sessionId, paymentMethod, actionButtons)
        if (!renderKey || renderKey === bemobiCheckoutRef.current.key) {
            return
        }

        bemobiCheckoutRef.current.key = renderKey
        bemobiCheckoutRef.current.checkoutUrl = checkoutUrl
        if (bemobiCheckoutRef.current.renewTimeoutId) {
            clearTimeout(bemobiCheckoutRef.current.renewTimeoutId)
            bemobiCheckoutRef.current.renewTimeoutId = null
        }
        bemobiCheckoutRef.current.renewInFlight = false
        bemobiCheckoutRef.current.renewAttempts = 0
        if (sessionId) {
            bemobiCheckoutRef.current.sessionId = sessionId
        }
        if (paymentMethod) {
            bemobiCheckoutRef.current.paymentMethod = paymentMethod
        }

        const wrap = document.createElement('li')
        wrap.setAttribute('data-bemobi-checkout', '')
        wrap.style.width = '100%'
        wrap.style.maxWidth = '100%'

        const card = document.createElement('div')
        card.className = 'bemobi-checkout-card'

        const frameWrap = document.createElement('div')
        frameWrap.className = 'bemobi-checkout-frame-wrap is-loading'

        const loading = document.createElement('div')
        loading.className = 'bemobi-checkout-loading'
        loading.textContent = 'Carregando pagamento...'

        const checkoutActions = document.createElement('div')
        checkoutActions.className = 'bemobi-checkout-actions'
        checkoutActions.style.display = 'none'
        actionButtons.forEach((buttonData) => {
            const btn = document.createElement('button')
            btn.className = 'btnAction btnMini bemobi-checkout-btn'
            btn.type = 'button'
            btn.textContent = asText(buttonData?.label) || asText(buttonData?.action) || 'Continuar'
            btn.onclick = () => {
                handleButtonAction(asText(buttonData?.action), btn.textContent, urlRedirect, btn)
            }
            checkoutActions.appendChild(btn)
        })

        const iframe = document.createElement('iframe')
        iframe.className = 'bemobi-checkout-frame'
        console.log('[Bemobi][Iframe URL]', checkoutUrl)
        iframe.src = checkoutUrl
        iframe.loading = 'lazy'
        iframe.referrerPolicy = 'strict-origin-when-cross-origin'
        iframe.allow = 'payment *; clipboard-write'
        iframe.setAttribute(
            'sandbox',
            'allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox allow-top-navigation-by-user-activation'
        )

        const frameReady = () => {
            frameWrap.classList.remove('is-loading')
            frameWrap.classList.add('is-ready')
            loading.style.display = 'none'
            if (actionButtons.length) {
                checkoutActions.style.display = 'flex'
            }
            if (bemobiCheckoutRef.current.key === renderKey) {
                bemobiCheckoutRef.current.renewTimeoutId = setTimeout(() => {
                    triggerCheckoutRenewal('session-expired')
                }, BEMOBI_RENEW_AFTER_MS)
            }
        }

        const frameError = () => {
            frameWrap.classList.remove('is-loading')
            frameWrap.classList.add('is-error')
            loading.style.display = 'block'
            loading.textContent = 'Atualizando pagamento...'
            triggerCheckoutRenewal('iframe-error', 300)
        }

        const triggerCheckoutRenewal = (reason, delayMs = 0) => {
            const run = async () => {
                const state = bemobiCheckoutRef.current
                if (state.key !== renderKey) return
                if (state.renewInFlight) return
                if ((state.renewAttempts || 0) >= BEMOBI_RENEW_MAX_ATTEMPTS) {
                    loading.textContent = 'Nao foi possivel atualizar o pagamento agora.'
                    return
                }

                state.renewInFlight = true
                state.renewAttempts = (state.renewAttempts || 0) + 1
                loading.style.display = 'block'
                loading.textContent = 'Atualizando pagamento...'
                checkoutActions.style.display = 'none'

                try {
                    const methodForRenewal = normalizeBemobiPaymentMethod(state.paymentMethod || paymentMethod) || 'SMART_CHECKOUT'
                    const refreshed = await fetchBemobiCheckoutForMethod(methodForRenewal, {forceRefresh: true})
                    if (!refreshed) {
                        throw new Error('Renovacao ignorada (checkout em voo).')
                    }
                } catch (error) {
                    console.debug('Falha ao renovar checkout Bemobi', {reason, attempt: state.renewAttempts}, error)
                    state.renewInFlight = false
                    if (state.key !== renderKey) return
                    loading.textContent = 'Falha ao atualizar pagamento. Tentando novamente...'
                    triggerCheckoutRenewal(`${reason}-retry`, 2500)
                    return
                }
                state.renewInFlight = false
            }

            if (delayMs > 0) {
                setTimeout(run, delayMs)
            } else {
                run()
            }
        }

        iframe.addEventListener('load', frameReady, {once: true})
        iframe.addEventListener('error', frameError, {once: true})
        setTimeout(() => {
            if (!frameWrap.classList.contains('is-loading')) {
                return
            }
            frameWrap.classList.remove('is-loading')
            frameWrap.classList.add('is-timeout')
            loading.style.display = 'block'
            loading.textContent = 'Atualizando pagamento...'
            triggerCheckoutRenewal('iframe-timeout', 300)
        }, 8000)

        frameWrap.appendChild(iframe)
        card.appendChild(frameWrap)
        card.appendChild(loading)
        if (actionButtons.length) {
            card.appendChild(checkoutActions)
        }
        wrap.appendChild(card)

        appendBotElement(wrap)
    }

    function normalizeActionText(action) {
        return asText(action)
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .toUpperCase()
    }

    function resolveBemobiPaymentMethodFromAction(action) {
        const normalized = normalizeActionText(action)
        if (!normalized) return ''
        if (normalized.includes('PIX')) return 'PIX'
        if (normalized.includes('CARTAO') || normalized.includes('CREDIT')) return 'CREDIT_CARD'
        return ''
    }

    function isBemobiPaymentSelectionAction(action) {
        const normalized = normalizeActionText(action)
        return normalized === 'PAGAR COM PIX' || normalized === 'PAGAR COM CARTAO'
    }

    function isBemobiPaymentValidationAction(action) {
        const normalized = normalizeActionText(action)
        if (!normalized) return false
        return normalized.startsWith('JA PAGUEI') ||
            normalized === 'PAGUEI O PIX' ||
            normalized === 'PAGUEI VIA PIX' ||
            normalized === 'PAGUEI NO CARTAO'
    }

    function buildBemobiCheckoutRequestKey(paymentMethod) {
        const session = asText(localStorage.getItem(STORAGE_SESSION))
        return `${session}|${paymentMethod}`
    }

    function clearBemobiMissingUrlRetry(paymentMethod) {
        const normalizedMethod = normalizeBemobiPaymentMethod(paymentMethod) || 'CREDIT_CARD'
        const retryKey = buildBemobiCheckoutRequestKey(normalizedMethod)
        const state = bemobiCheckoutRef.current
        if (state?.missingUrlRetryTimers?.[retryKey]) {
            clearTimeout(state.missingUrlRetryTimers[retryKey])
            delete state.missingUrlRetryTimers[retryKey]
        }
        if (state?.missingUrlRetryAttempts?.[retryKey]) {
            delete state.missingUrlRetryAttempts[retryKey]
        }
    }

    function triggerBemobiMissingUrlRetry(paymentMethod, reason = 'missing-checkout-url') {
        const normalizedMethod = normalizeBemobiPaymentMethod(paymentMethod) || 'CREDIT_CARD'
        const retryKey = buildBemobiCheckoutRequestKey(normalizedMethod)
        const state = bemobiCheckoutRef.current
        if (!state.missingUrlRetryAttempts) {
            state.missingUrlRetryAttempts = {}
        }
        if (!state.missingUrlRetryTimers) {
            state.missingUrlRetryTimers = {}
        }
        if (state.missingUrlRetryTimers[retryKey]) {
            return
        }
        const attempts = state.missingUrlRetryAttempts[retryKey] || 0
        if (attempts >= BEMOBI_MISSING_URL_RETRY_MAX_ATTEMPTS) {
            return
        }
        const nextAttempt = attempts + 1
        state.missingUrlRetryAttempts[retryKey] = nextAttempt
        const delayMs = Math.min(BEMOBI_MISSING_URL_RETRY_BASE_DELAY_MS * nextAttempt, 5000)
        state.missingUrlRetryTimers[retryKey] = setTimeout(async () => {
            delete state.missingUrlRetryTimers[retryKey]
            try {
                const refreshed = await fetchBemobiCheckoutForMethod(normalizedMethod, {forceRefresh: true})
                if (!refreshed) {
                    throw new Error('Renovacao ignorada (checkout em voo).')
                }
            } catch (error) {
                console.debug('Falha ao recuperar checkout Bemobi sem URL', {reason, attempt: nextAttempt, paymentMethod: normalizedMethod}, error)
                triggerBemobiMissingUrlRetry(normalizedMethod, `${reason}-retry`)
            }
        }, delayMs)
    }

    async function fetchBemobiCheckoutForMethod(paymentMethod, {forceRefresh = false} = {}) {
        const normalizedMethod = normalizeBemobiPaymentMethod(paymentMethod) || 'CREDIT_CARD'
        const requestKey = buildBemobiCheckoutRequestKey(normalizedMethod)
        const now = Date.now()
        const lastRequestedAt = bemobiCheckoutRef.current.refreshRequestedAt[requestKey] || 0

        if (!forceRefresh && now - lastRequestedAt < 15000) {
            return null
        }

        if (bemobiCheckoutRef.current.refreshInFlight && bemobiCheckoutRef.current.refreshKey === requestKey) {
            return null
        }

        bemobiCheckoutRef.current.refreshInFlight = true
        bemobiCheckoutRef.current.refreshKey = requestKey
        bemobiCheckoutRef.current.refreshRequestedAt[requestKey] = now

        try {
            const checkoutPayload = await bot.gerarCheckoutBemobi({
                paymentMethod: normalizedMethod,
                forceRefresh,
            })
            const checkout = resolveBemobiCheckoutPayload(checkoutPayload)
            if (!checkout) {
                return checkoutPayload
            }
            const mergedPayload = {
                ...checkoutPayload,
                sessionId: checkoutPayload?.sessionId || localStorage.getItem(STORAGE_SESSION) || '',
                bemobiCheckout: checkout,
                contexto: {
                    ...(checkoutPayload?.contexto || {}),
                    bemobiCheckout: checkout,
                },
                props: {
                    ...(checkoutPayload?.props || {}),
                    requiresBemobiCheckout: true,
                    bemobiPaymentMethod: normalizedMethod,
                    buttons: Array.isArray(checkoutPayload?.props?.buttons) && checkoutPayload.props.buttons.length
                        ? checkoutPayload.props.buttons
                        : bemobiCheckoutRef.current.checkoutButtons,
                },
            }
            cacheBemobiCheckout(mergedPayload)
            appendBemobiCheckoutIframe(mergedPayload)
            maybeStartPixPolling(mergedPayload)
            return checkoutPayload
        } finally {
            bemobiCheckoutRef.current.refreshInFlight = false
        }
    }

    /** ===== Renderização das respostas do backend ===== */
    function handleResponse(data) {
        const BASE_INFO_DELAY_MS = 1500
        const HIGHLIGHT_DELAY_MS = 3500

        setDisableEnviar(false)
        document.querySelectorAll("#chat #messages button").forEach(e => {
            e.setAttribute('disabled', true)
        })
        // suporte ao formato atual: html + props + attachments + buttons + contexto.pix
        if (!data) return
        maybeStartPixPolling(data)
        const activePlaceholder = getActiveBotPlaceholder(true)
        const delay = getPlaceholderDelay(activePlaceholder)
        handleBotEventFromResponse(data)
        cacheBemobiCheckout(data)

        const infoHtml = data?.props?.infoHtml || data?.infoHtml
        const hasInfoHtml = !!infoHtml
        const hasClipboard = Object.prototype.hasOwnProperty.call(data?.props || {}, 'clipboardText')
        const clipboardText = asText(data?.props?.clipboardText)
        if (clipboardText && clipboardText !== lastClipboardTextRef.current) {
            lastClipboardTextRef.current = clipboardText
            copyToClipboard(clipboardText)
                .then((copied) => {
                    if (copied) {
                        appendMe('Conteudo copiado.')
                        scrollToEnd()
                    }
                })
                .catch(() => {
                    // ignora
                })
            if (/^https?:\/\//i.test(clipboardText)) {
                bemobiCheckoutRef.current.checkoutUrl = clipboardText
            }
        }
        const isClipboardInfo = hasInfoHtml && hasClipboard
        const hasSecondaryContent = hasSecondaryPayload(data)
        const hasMediaInInfoHtml = hasVisualMediaMarkup(infoHtml)
        const hasMediaInMainHtml = hasVisualMediaMarkup(data?.html)
        const hasHighlightInfo = hasInfoHtml && (
            isApprovalHighlightMarkup(infoHtml) ||
            isDiscountHighlightMarkup(infoHtml) ||
            hasMediaInInfoHtml
        )
        const shouldUseLongDelay = !isClipboardInfo && (
            hasHighlightInfo ||
            (hasMediaInMainHtml && hasSecondaryContent)
        )
        const interBlockDelayMs = shouldUseLongDelay ? HIGHLIGHT_DELAY_MS : BASE_INFO_DELAY_MS
        const shouldDelayPlaceholder = hasInfoHtml && !isClipboardInfo && hasSecondaryContent

        const renderSecondaryPayload = () => {
            if (data?.props?.attachments?.length) {
                data.props.attachments.forEach((a) => appendPDF(a, /*isBase64*/ false))
            }
            if (data?.contexto?.contratoPdfB64 && data?.props?.autoGenerateContract) {
                appendPDF({
                    name: data?.contexto?.contratoFilename, downloadUrl: data?.contexto?.contratoPdfB64,
                }, true)
            }
            // guarda PIX (pra ação copiar)
            pixCodeRef.current = data?.contexto?.pix?.code || pixCodeRef.current
            appendBemobiCheckoutIframe(data)

            const buttons = Array.isArray(data?.props?.buttons) ? data.props.buttons : []
            if (buttons.length) {
                if (!data?.props?.requiresBemobiCheckout) {
                    appendButtons(buttons, data?.props?.url_redirect || '')
                }
                setDisableEnviar(true)
            }
            if (data?.items && data?.items?.bensCredito) {
                const listPlaceholder = enqueueBotPlaceholder({reserve: true})
                appendList(data?.items?.bensCredito, listPlaceholder)
                setDisableEnviar(true)
            }
        }

        const renderPayload = (allowHtml = false, options = {}) => {
            const skipHtml = options?.skipHtml === true
            if (!skipHtml && data.html && (allowHtml || !hasInfoHtml)) appendBotHTML(data.html)
            renderSecondaryPayload()
        }

        if (hasInfoHtml) {
            const renderInfoBlock = () => {
                appendBotHTML(infoHtml)
                if (isClipboardInfo) {
                    renderPayload(false)
                    cleanupUnusedBotPlaceholder()
                    return
                }
                if (shouldDelayPlaceholder) {
                    enqueueBotPlaceholder({reserve: true})
                }
                setTimeout(() => {
                    renderPayload(true)
                    cleanupUnusedBotPlaceholder()
                }, interBlockDelayMs)
            }
            if (delay > 0) {
                setTimeout(renderInfoBlock, delay)
            } else {
                renderInfoBlock()
            }
            return
        }

        if (hasMediaInMainHtml && hasSecondaryContent) {
            const renderVisualThenPayload = () => {
                appendBotHTML(data.html)
                setTimeout(() => {
                    renderPayload(false, {skipHtml: true})
                    cleanupUnusedBotPlaceholder()
                }, HIGHLIGHT_DELAY_MS)
            }
            if (delay > 0) {
                setTimeout(renderVisualThenPayload, delay)
            } else {
                renderVisualThenPayload()
            }
            return
        }

        if (delay > 0) {
            setTimeout(() => {
                renderPayload()
                cleanupUnusedBotPlaceholder()
            }, delay)
            return
        }
        renderPayload()
        cleanupUnusedBotPlaceholder()
    }

    function appendMe(text) {
        messagesRef.current?.insertAdjacentHTML('beforeend', `<li me><p>${escapeHtml(text)}</p></li>`)
    }

    function appendMeHTML(text) {
        messagesRef.current?.insertAdjacentHTML('beforeend', `<li me><p>${text}</p></li>`)
    }

    function appendBotHTML(html) {
        const placeholder = claimBotPlaceholder()
        if (placeholder) {
            placeholder.setAttribute('data-html-msg', '')
            swapPlaceholderWithHtml(placeholder, html)
            scrollToEnd()
            return
        }
        const li = document.createElement('li')
        li.setAttribute('data-html-msg', '')
        li.style.width = '100%'
        li.innerHTML = html
        enhanceMediaPlaceholders(li)
        applyBotFadeIn(li)
        messagesRef.current?.appendChild(li)
        scrollToEnd()
    }

    function appendBotElement(element) {
        const placeholder = claimBotPlaceholder()
        if (placeholder) {
            swapPlaceholderWithElement(placeholder, element)
            scrollToEnd()
            return
        }
        applyBotFadeIn(element)
        messagesRef.current?.insertAdjacentElement('beforeend', element)
        scrollToEnd()
    }

    function applyBotFadeIn(element) {
        if (!element || element.nodeType !== 1) return
        if (element.tagName !== 'LI') return
        element.classList.add('bot-response')
        requestAnimationFrame(() => {
            element.classList.add('bot-response-ready')
        })
    }

    function enqueueBotPlaceholder(options = {}) {
        const {reserve = false} = options
        ensureBotPlaceholderStyles()
        const li = document.createElement('li')
        li.className = 'bot-response-placeholder bot-response-placeholder-typing'
        li._placeholderStart = performance.now()
        li._placeholderMinMs = 300
        if (reserve) {
            li.dataset.placeholderReserved = 'true'
        }
        const typing = document.createElement('div')
        typing.className = 'bot-placeholder-typing'
        const typingLabel = document.createElement('span')
        typingLabel.className = 'bot-placeholder-typing-label'
        typingLabel.textContent = 'Ka esta digitando'
        const typingDots = document.createElement('span')
        typingDots.className = 'bot-placeholder-typing-dots'
        typingDots.setAttribute('aria-hidden', 'true')
        for (let i = 0; i < 3; i += 1) {
            const dot = document.createElement('span')
            typingDots.appendChild(dot)
        }
        typing.appendChild(typingLabel)
        typing.appendChild(typingDots)
        li.appendChild(typing)
        li._placeholderShimmer = null
        messagesRef.current?.appendChild(li)
        pendingBotPlaceholdersRef.current.push(li)
        scheduleScrollToEnd()
        return li
    }

    function consumeBotPlaceholder() {
        const li = activeBotPlaceholderRef.current || pendingBotPlaceholdersRef.current.shift()
        if (!li) return
        activeBotPlaceholderRef.current = null
        const elapsed = performance.now() - (li._placeholderStart || 0)
        const delay = Math.max(0, (li._placeholderMinMs || 300) - elapsed)
        setTimeout(() => {
            const shimmer = li._placeholderShimmer
            if (shimmer) {
                stopShimmerAnimation(shimmer)
            }
            li.classList.add('bot-response-placeholder-fade')
            setTimeout(() => {
                li.remove()
            }, 180)
        }, delay)
    }

    function claimBotPlaceholder() {
        const li = getActiveBotPlaceholder(true)
        if (!li) return null
        activeBotPlaceholderRef.current = null
        if (li.dataset) {
            delete li.dataset.placeholderReserved
        }
        return li
    }

    function cleanupUnusedBotPlaceholder() {
        if (activeBotPlaceholderRef.current) {
            consumeBotPlaceholder()
        }
        if (pendingBotPlaceholdersRef.current.length > 0) {
            pendingBotPlaceholdersRef.current.forEach((li) => {
                if (!li) return
                if (li.dataset?.placeholderReserved) return
                const shimmer = li._placeholderShimmer || li.querySelector('.media-shimmer, .bot-placeholder-shimmer')
                if (shimmer) {
                    stopShimmerAnimation(shimmer)
                }
                li.classList.add('bot-response-placeholder-fade')
                setTimeout(() => {
                    li.remove()
                }, 180)
            })
            pendingBotPlaceholdersRef.current = pendingBotPlaceholdersRef.current.filter((li) => li?.dataset?.placeholderReserved)
        }
    }

    function getActiveBotPlaceholder(includeReserved = false) {
        if (activeBotPlaceholderRef.current) return activeBotPlaceholderRef.current
        const pending = pendingBotPlaceholdersRef.current
        let index = pending.findIndex((li) => !li?.dataset?.placeholderReserved)
        if (index === -1 && includeReserved) {
            index = pending.findIndex((li) => li?.dataset?.placeholderReserved)
        }
        if (index === -1) {
            return null
        }
        const [next] = pending.splice(index, 1)
        activeBotPlaceholderRef.current = next || null
        return activeBotPlaceholderRef.current
    }

    function getPlaceholderDelay(placeholder) {
        if (!placeholder) return 0
        const elapsed = performance.now() - (placeholder._placeholderStart || 0)
        return Math.max(0, (placeholder._placeholderMinMs || 300) - elapsed)
    }

    function swapPlaceholderWithHtml(placeholder, html) {
        const shimmer = placeholder._placeholderShimmer || placeholder.querySelector('.media-shimmer, .bot-placeholder-shimmer')
        placeholder.classList.remove('bot-response-placeholder', 'bot-response-placeholder-fade')
        if (placeholder.dataset) {
            delete placeholder.dataset.placeholderReserved
        }
        removePendingPlaceholder(placeholder)
        placeholder.style.width = '100%'
        placeholder.style.maxWidth = ''
        const card = placeholder.querySelector('.bot-placeholder-card')
        if (card) {
            card.remove()
        }
        while (placeholder.firstChild) {
            placeholder.removeChild(placeholder.firstChild)
        }
        if (shimmer) {
            placeholder.appendChild(shimmer)
        }
        const content = document.createElement('div')
        content.innerHTML = html
        placeholder.appendChild(content)
        enhanceMediaPlaceholders(placeholder)
        const hasMedia = !!placeholder.querySelector('video, iframe, img')
        if (!hasMedia) {
            const activeShimmer = shimmer || placeholder.querySelector('.media-shimmer, .bot-placeholder-shimmer')
            if (activeShimmer) {
                stopShimmerAnimation(activeShimmer)
                activeShimmer.remove()
            }
        }
        applyBotFadeIn(placeholder)
    }

    function swapPlaceholderWithElement(placeholder, element) {
        const shimmer = placeholder._placeholderShimmer
        if (shimmer) {
            stopShimmerAnimation(shimmer)
        }
        placeholder.classList.remove('bot-response-placeholder', 'bot-response-placeholder-fade')
        if (placeholder.dataset) {
            delete placeholder.dataset.placeholderReserved
        }
        removePendingPlaceholder(placeholder)
        placeholder.style.width = ''
        placeholder.style.maxWidth = ''
        placeholder.replaceWith(element)
        applyBotFadeIn(element)
    }

    function removePendingPlaceholder(target) {
        const pending = pendingBotPlaceholdersRef.current
        const index = pending.indexOf(target)
        if (index >= 0) {
            pending.splice(index, 1)
        }
    }

    function enhanceMediaPlaceholders(container) {
        if (!container) return
        let contentWrapper = container.querySelector('.media-content')
        if (!contentWrapper) {
            contentWrapper = document.createElement('div')
            contentWrapper.className = 'media-content'
            const children = Array.from(container.childNodes)
            children.forEach((child) => {
                if (child.nodeType === 1) {
                    const el = child
                    if (el.classList?.contains('media-shimmer') || el.classList?.contains('bot-placeholder-shimmer')) {
                        return
                    }
                }
                contentWrapper.appendChild(child)
            })
            container.appendChild(contentWrapper)
        }

        const medias = Array.from(contentWrapper.querySelectorAll('video, iframe, img'))
        if (medias.length === 0) return

        const startTime = Date.now()
        container.classList.add('media-message', 'media-loading')
        container.style.position = 'relative'
        container.style.overflow = 'hidden'
        const messagesEl = messagesRef.current
        const shouldAnchor = messagesEl
            ? (messagesEl.scrollHeight - messagesEl.scrollTop - messagesEl.clientHeight) < 40
            : false
        if (shouldAnchor) {
            container._anchorUntil = Math.max(container._anchorUntil || 0, performance.now() + 500)
            if (!container._anchorRaf) {
                const tick = () => {
                    if (!container._anchorUntil || performance.now() > container._anchorUntil) {
                        container._anchorRaf = null
                        container._anchorUntil = null
                        return
                    }
                    scrollToEnd()
                    container._anchorRaf = requestAnimationFrame(tick)
                }
                container._anchorRaf = requestAnimationFrame(tick)
            }
        }
        ensureShimmerStyles()
        let shimmer = container.querySelector('.media-shimmer, .bot-placeholder-shimmer')
        if (!shimmer) {
            shimmer = document.createElement('div')
            shimmer.className = 'media-shimmer'
            shimmer.style.position = 'absolute'
            shimmer.style.top = '0'
            shimmer.style.left = '0'
            shimmer.style.width = '100%'
            shimmer.style.height = '100%'
            shimmer.style.top = '0'
            shimmer.style.left = '0'
            shimmer.style.borderRadius = '12px'
            shimmer.style.backgroundColor = 'rgba(11, 33, 69, 0.35)'
            shimmer.style.backgroundImage = 'linear-gradient(90deg, rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.45), rgba(255, 255, 255, 0.05))'
            shimmer.style.backgroundSize = '300% 100%'
            shimmer.style.backgroundPosition = '-100% 0%'
            shimmer.style.animation = 'none'
            shimmer.style.zIndex = '1'
            shimmer.style.opacity = '1'
            shimmer.style.willChange = 'transform'
            shimmer.style.pointerEvents = 'none'
            container.insertBefore(shimmer, container.firstChild)
        } else {
            shimmer.classList.add('media-shimmer')
            if (shimmer.parentElement !== container) {
                container.insertBefore(shimmer, container.firstChild)
            } else if (container.firstChild !== shimmer) {
                container.insertBefore(shimmer, container.firstChild)
            }
        }
        startShimmerAnimation(shimmer)
        let pending = medias.length
        let revealed = false

        const revealAll = () => {
            if (revealed) return
            revealed = true
            if (shouldAnchor) {
                container._anchorUntil = Math.max(container._anchorUntil || 0, performance.now() + 500)
            }
            container.classList.remove('media-loading')
            container.classList.add('media-loaded')
            if (shimmer) {
                shimmer.style.opacity = '0'
                shimmer.style.transition = 'opacity 200ms ease'
            }
            setTimeout(() => {
                if (container._anchorRaf) {
                    cancelAnimationFrame(container._anchorRaf)
                    container._anchorRaf = null
                }
                container._anchorUntil = null
                stopShimmerAnimation(shimmer)
                shimmer?.remove()
                container.style.overflow = ''
                scheduleScrollToEnd()
            }, 200)
        }
        const markLoaded = () => {
            scheduleScrollToEnd()
            pending -= 1
            if (pending <= 0) {
                const elapsed = Date.now() - startTime
                const delay = Math.max(0, 300 - elapsed)
                setTimeout(revealAll, delay)
            }
        }

        applyMediaSizing(medias)
        medias.forEach((media) => {
            const tag = media.tagName
            media.classList.add('media-element')
            media.style.opacity = '0'
            media.style.transition = 'opacity 200ms ease'
            let done = false
            let rafId = null
            let timeoutId = null
            const markLoadedOnce = () => {
                if (done) return
                done = true
                media.style.opacity = '1'
                if (rafId) {
                    cancelAnimationFrame(rafId)
                    rafId = null
                }
                if (timeoutId) {
                    clearTimeout(timeoutId)
                    timeoutId = null
                }
                markLoaded()
            }
            const startPolling = (checkFn) => {
                const tick = () => {
                    if (done) return
                    if (checkFn()) {
                        markLoadedOnce()
                        return
                    }
                    rafId = requestAnimationFrame(tick)
                }
                rafId = requestAnimationFrame(tick)
            }
            timeoutId = setTimeout(markLoadedOnce, 4000)

            if (tag === 'IMG') {
                if (media.complete) {
                    markLoadedOnce()
                } else {
                    media.addEventListener('load', () => {
                        markLoadedOnce()
                    }, {once: true})
                    media.addEventListener('error', () => {
                        markLoadedOnce()
                    }, {once: true})
                    startPolling(() => media.complete && media.naturalWidth > 0 && media.naturalHeight > 0)
                }
                return
            }

            if (tag === 'VIDEO') {
                const hasFrame = media.readyState >= 2 && media.videoWidth > 0 && media.videoHeight > 0
                if (hasFrame) {
                    markLoadedOnce()
                } else {
                    media.addEventListener('loadedmetadata', () => {
                        markLoadedOnce()
                    }, {once: true})
                    media.addEventListener('loadeddata', () => {
                        markLoadedOnce()
                    }, {once: true})
                    media.addEventListener('canplay', () => {
                        markLoadedOnce()
                    }, {once: true})
                    media.addEventListener('canplaythrough', () => {
                        markLoadedOnce()
                    }, {once: true})
                    media.addEventListener('error', () => {
                        markLoadedOnce()
                    }, {once: true})
                    startPolling(() => media.readyState >= 2 && media.videoWidth > 0 && media.videoHeight > 0)
                }
                return
            }

            if (tag === 'IFRAME') {
                media.addEventListener('load', () => {
                    markLoadedOnce()
                }, {once: true})
                media.addEventListener('error', () => {
                    markLoadedOnce()
                }, {once: true})
            }
        })
    }

    function applyMediaSizing(elements) {
        elements.forEach((media) => {
            const tag = media.tagName
            if (tag === 'VIDEO' || tag === 'IFRAME') {
                media.style.width = '100%'
                if (!media.style.aspectRatio) {
                    media.style.aspectRatio = '16 / 9'
                }
                if (!media.style.height) {
                    media.style.height = 'auto'
                }
            } else if (tag === 'IMG') {
                media.style.width = '100%'
                if (!media.style.height) {
                    media.style.height = 'auto'
                }
            }
        })
    }

    function ensureShimmerStyles() {
        if (typeof document === 'undefined') return
        if (document.getElementById('chat-media-shimmer-style')) return
        const style = document.createElement('style')
        style.id = 'chat-media-shimmer-style'
        style.textContent = `
.media-message { position: relative; width: 100%; }
.media-message.media-loading { overflow: hidden; }
.media-message.media-loading::before { display: none !important; }
.media-content { position: relative; transition: opacity 200ms ease; }
.media-message.media-loading .media-content { opacity: 0; pointer-events: none; }
.media-message.media-loaded .media-content { opacity: 1; }
.media-element { width: 100%; border-radius: 12px; display: block; }
.media-shimmer { animation: none !important; }
.bot-response { opacity: 0; transform: translateY(4px); transition: opacity 200ms ease, transform 200ms ease; }
.bot-response.bot-response-ready { opacity: 1; transform: translateY(0); }
.bot-response-placeholder { width: 100%; display: flex; justify-content: flex-start; padding: 0; background: transparent; }
.bot-response-placeholder-typing { width: auto !important; max-width: 100% !important; margin-bottom: 10px; }
.bot-placeholder-typing { position: relative; z-index: 2; display: inline-flex; align-items: center; gap: 8px; padding: 2px 4px; color: #6b7280; font-size: 13px; font-weight: 600; letter-spacing: 0.1px; background: transparent; }
.bot-placeholder-typing-label { line-height: 1; }
.bot-placeholder-typing-dots { display: inline-flex; align-items: center; gap: 4px; }
.bot-placeholder-typing-dots span { width: 6px; height: 6px; border-radius: 50%; background: #6b7280; opacity: 0.35; animation: botTypingDot 1s infinite ease-in-out; }
.bot-placeholder-typing-dots span:nth-child(2) { animation-delay: 0.15s; }
.bot-placeholder-typing-dots span:nth-child(3) { animation-delay: 0.3s; }
.bot-response-placeholder-fade { opacity: 0; transition: opacity 180ms ease; }
@keyframes botTypingDot {
    0%, 80%, 100% { transform: translateY(0); opacity: 0.35; }
    40% { transform: translateY(-2px); opacity: 1; }
}
        `
        document.head.appendChild(style)
    }

    function ensureBotPlaceholderStyles() {
        ensureShimmerStyles()
    }

    function startShimmerAnimation(element) {
        if (!element) return
        if (element._shimmerRaf) return
        const start = performance.now()
        const step = (now) => {
            const elapsed = now - start
            const pct = 100 - ((elapsed % 2000) / 2000) * 200
            element.style.backgroundPosition = `${pct}% 0%`
            element._shimmerRaf = requestAnimationFrame(step)
        }
        element._shimmerRaf = requestAnimationFrame(step)
    }

    function stopShimmerAnimation(element) {
        if (!element || !element._shimmerRaf) return
        cancelAnimationFrame(element._shimmerRaf)
        element._shimmerRaf = null
    }

    function appendPDF(attachment, isBase64) {
        const {name, downloadUrl} = attachment || {}
        const displayName = name || 'arquivo.pdf'
        const safeName = buildPdfName(displayName)
        const li = document.createElement('li')
        li.style.cursor = 'pointer'
        li.innerHTML = `
      <div class="d-flex align-items-center pdf-preview-row">
        <div class="pdf-content mr-2"></div>
        <div class="pdf-info" style="margin-left:10px;">
          <p style="margin:0;color:#9cb9ed;font-weight:600;">${escapeHtml(displayName)}</p>
         
        </div>
      </div>`
        const previewBox = li.querySelector('.pdf-content')
        if (previewBox) {
            previewBox.textContent = 'PDF'
        }

        const fallbackDownload = async () => {
            try {
                if (isBase64) {
                    bot.downloadPDFBase64(downloadUrl, safeName)
                } else {
                    await bot.downloadPDF(safeName, downloadUrl)
                }
            } catch (e) {
                console.error(e)
            }
        }
        li.onclick = fallbackDownload
        appendBotElement(li)

        const pdfBlobPromise = isBase64 ? Promise.resolve(pdfBase64ToBlob(downloadUrl)) : downloadUrl ? bot.fetchPDFBlob?.(downloadUrl) : null

        if (!pdfBlobPromise) {
            return
        }

        pdfBlobPromise
            .then(async (blob) => {
                if (!blob) return
                const previewSrc = await createPdfPreviewFromBlob(blob)
                if (previewSrc && previewBox) {
                    const img = document.createElement('img')
                    img.src = previewSrc
                    img.alt = `Prévia de ${displayName}`
                    img.style.width = '90px'
                    img.style.height = '100px'
                    img.style.objectFit = 'cover'
                    img.style.objectPosition = 'top'
                    img.style.borderRadius = '10px'
                    previewBox.innerHTML = ''
                    previewBox.appendChild(img)
                }
                li.onclick = () => openPdfBlob(blob, safeName, {downloadUrl, isBase64})
            })
            .catch((err) => {
                console.error('Falha ao preparar pré-visualização do PDF', err)
            })
    }

    async function createPdfPreviewFromBlob(blob) {
        try {
            const pdfjsLib = await loadPdfJs()
            const arrayBuffer = await blob.arrayBuffer()
            const pdf = await pdfjsLib.getDocument({data: arrayBuffer}).promise
            const page = await pdf.getPage(1)

            const viewport = page.getViewport({scale: 1.5})
            const canvas = document.createElement('canvas')
            const context = canvas.getContext('2d')
            canvas.height = viewport.height / 4
            canvas.width = viewport.width / 4

            await page.render({canvasContext: context, viewport}).promise

            const thumbWidth = 90
            const thumbHeight = 100
            const offsetX = +25 // translate x
            const offsetY = +25 // translate y
            const cropWidth = canvas.width - offsetX
            const cropHeight = Math.min(canvas.height - offsetY, Math.round(canvas.height * 0.8))

            const thumbCanvas = document.createElement('canvas')
            thumbCanvas.width = thumbWidth
            thumbCanvas.height = thumbHeight
            const thumbCtx = thumbCanvas.getContext('2d')

            // desenha o conteúdo com deslocamento
            thumbCtx.drawImage(canvas, offsetX, offsetY, cropWidth, cropHeight, 0, // dx
                0, // dy
                thumbWidth, thumbHeight)

            return thumbCanvas.toDataURL('image/png')
        } catch (error) {
            console.error('Erro ao renderizar prévia do PDF', error)
            return null
        }
    }

    async function openPdfBlob(blob, safeName, {downloadUrl, isBase64}) {
        if (!blob) return

        try {
            if (downloadUrl) {
                await downloadPdfInBackground(downloadUrl, {isBase64, filename: safeName})
            } else {
                await downloadPdfInBackground(blob, {filename: safeName})
            }
        } catch (error) {
            console.error('Download em segundo plano falhou, usando fallback', error)
            downloadBlob(blob, safeName)
        }
    }

    async function downloadPdfInBackground(source, {isBase64 = false, filename = 'arquivo.pdf', delayMs = 250} = {}) {
        let blob = null
        if (source instanceof Blob) {
            blob = source
        } else if (isBase64) {
            blob = pdfBase64ToBlob(source)
        } else if (source) {
            blob = await bot.fetchPDFBlob?.(source)
        }
        if (!blob) {
            throw new Error('PDF inválido para download em segundo plano')
        }

        await new Promise((resolve) => setTimeout(resolve, delayMs))
        downloadBlob(blob, filename)
    }

    function appendButtons(buttons, urlRedirect) {
        const wrap = document.createElement('li')
        wrap.setAttribute('btn', '')
        wrap.style.width = '100%'
        wrap.style.maxWidth = '100%'
        wrap.innerHTML = `<div class="container"><div class="row"></div></div>`
        const row = wrap.querySelector('.row')

        buttons
            .filter((b) => b.action)
            .forEach((b) => {
                const col = document.createElement('div')
                col.className = 'col-6'
                const btn = document.createElement('button')
                btn.className = 'btnAction btnMini'
                btn.textContent = b.label
                btn.onclick = () => handleButtonAction(b.action, b.label, urlRedirect, btn)
                col.appendChild(btn)
                row.appendChild(col)
            })

        const placeholder = claimBotPlaceholder()
        if (placeholder) {
            swapPlaceholderWithElement(placeholder, wrap)
        } else {
            messagesRef.current?.appendChild(wrap)
        }
        scrollToEnd()
    }

    function appendImage(base64) {
        const wrap = document.createElement('li')
        wrap.setAttribute("me", true)
        wrap.style.width = '100%'
        wrap.style.maxWidth = '85%'
        wrap.innerHTML = `<img src="${base64}" />`

        messagesRef.current?.appendChild(wrap)
        scrollToEnd()
    }

    function appendList(lista, placeholder) {
        console.log(lista)
        scrollToEnd()

        setTimeout(() => {
            const container = document.createElement('div')
            container.className = ''
            container.id = ''

            lista?.forEach((benCredito) => {
                const details = document.createElement('details')
                details.className = 'detail-acordion credit-list-item'
                details.ontoggle = function fecharOutros(e) {
                    const current = e.currentTarget

                    if (e.currentTarget.open) {
                        current.querySelector('img').style.transform = 'rotate(180deg)'
                    } else {
                        current.querySelector('img').style.transform = 'rotate(0deg)'
                    }

                    if (current.open) {
                        document
                            .querySelectorAll('.detail-acordion[open]')
                            .forEach((element) => {
                                if (element !== current) {
                                    element.open = false
                                }
                            })
                    }
                }

                const summary = document.createElement('summary')
                summary.className = 'd-flex justify-content-between pt-4 pb-4 credit-list-summary'

                const span = document.createElement('span')
                span.className = 'credit-list-title'
                span.textContent = benCredito.credito

                const imgIcon = document.createElement('img')
                imgIcon.style.width = '12px'
                imgIcon.src = '/Ksk_Consorcio_files/arrow-down-sign-to-navigate.png'

                summary.appendChild(span)
                summary.appendChild(imgIcon)

                const contentDiv = document.createElement('div')
                contentDiv.appendChild(montarBens(benCredito.bens))

                details.appendChild(summary)
                details.appendChild(contentDiv)
                container.appendChild(details)
            })

            const li = document.createElement('li')
            li.style = 'width:100%'
            li.appendChild(container)

            if (placeholder) {
                swapPlaceholderWithElement(placeholder, li)
            } else {
                appendBotElement(li)
            }
        }, 3000)
    }

    async function selecionarLista(codigo, credito, grupo, prazo, assembleia, planCode, parcela, restanteParcela, wppTituloCredito, msg1, msg2) {
        document.querySelectorAll('.btn-acordion')?.forEach((btn) => {
            console.log(btn)
            btn.setAttribute('disabled', true)
        })

        appendMeHTML(`${msg1}<br />${msg2}`)
        try {
            enqueueBotPlaceholder()
            var response = await bot.enviarPlanoSelecionado({
                codigo: codigo,
                credito: credito,
                grupo: grupo,
                prazo: prazo,
                assembleia: assembleia,
                planCode: planCode,
                parcela: parcela,
                restanteParcela: restanteParcela,
                wppTituloCredito: wppTituloCredito,
            })

            handleResponse(response)
        } catch (e) {
            consumeBotPlaceholder()
            console.error(e)
        }
    }

    function montarBens(bens) {
        const container = document.createElement('div')

        bens?.forEach((benCredito) => {
            const itemDiv = document.createElement('div')
            itemDiv.className = 'credit-list-plan-item'

            const titleText = document.createTextNode(benCredito.wppTituloParcela)
            itemDiv.appendChild(titleText)

            const br = document.createElement('br')
            itemDiv.appendChild(br)

            const tagDiv = document.createElement('div')
            tagDiv.className = 'credit-list-plan-tag'

            const tagSpan = document.createElement('span')
            tagSpan.className = 'tag'
            tagSpan.textContent = benCredito.wppDescParcela
            tagDiv.appendChild(tagSpan)

            const button = document.createElement('button')
            button.className = 'btnAction btnMini btnActionWhite btn-acordion'
            button.textContent = 'Selecionar'
            button.addEventListener('click', function () {
                selecionarLista(benCredito.codigo, benCredito.credito, benCredito.grupo, benCredito.prazo, benCredito.assembleia, benCredito.planCode, benCredito.parcela, benCredito.restanteParcela, benCredito.wppTituloCredito, benCredito.wppTituloParcela, benCredito.wppDescParcela)
            })
            //button.onclick = () => alert('aioba')

            const hr = document.createElement('hr')

            itemDiv.appendChild(tagDiv)
            itemDiv.appendChild(button)
            container.appendChild(itemDiv)
            container.appendChild(hr)
        })

        return container
    }

    const openDetailsSummary = (e) => {
        console.log('click')
        console.log(e)
    }

    async function handleButtonAction(action, label, urlRedirect, btnEl) {
        try {
            const selectedPaymentMethod = resolveBemobiPaymentMethodFromAction(action)
            if (selectedPaymentMethod) {
                bemobiCheckoutRef.current.paymentMethod = selectedPaymentMethod
            }
            if (isBemobiPaymentValidationAction(action)) {
                bemobiCheckoutRef.current.key = ''
                if (bemobiCheckoutRef.current.renewTimeoutId) {
                    clearTimeout(bemobiCheckoutRef.current.renewTimeoutId)
                    bemobiCheckoutRef.current.renewTimeoutId = null
                }
            }
            if (isBemobiPaymentSelectionAction(action) && selectedPaymentMethod) {
                fetchBemobiCheckoutForMethod(selectedPaymentMethod, {forceRefresh: false})
                    .catch((error) => {
                        console.debug('Falha ao preparar checkout Bemobi na selecao de pagamento', error)
                    })
            }
            if (action === 'REDIRECT' && urlRedirect) {
                window.open(urlRedirect, '_blank')?.focus()
                return
            }
            if (action === 'COPIAR CODIGO PIX') {
                if (pixCodeRef.current) {
                    const copied = await copyToClipboard(pixCodeRef.current)
                    appendMe(copied ? 'PIX copiado.' : 'Nao foi possivel copiar automaticamente o codigo PIX.')
                } else {
                    appendMe('Codigo PIX indisponivel no momento.')
                }
                scrollToEnd()
                enqueueBotPlaceholder()
                const resp = await bot.enviarButton(action)
                handleResponse(resp)
                return
            }
            if (action === 'COPIAR LINK CHECKOUT' || action === 'COPIAR LINK DO CHECKOUT') {
                let checkoutUrl = asText(bemobiCheckoutRef.current.checkoutUrl)
                if (!checkoutUrl) {
                    try {
                        const paymentMethod = normalizeBemobiPaymentMethod(bemobiCheckoutRef.current.paymentMethod) || 'CREDIT_CARD'
                        const checkoutPayload = await fetchBemobiCheckoutForMethod(paymentMethod, {forceRefresh: true})
                        cacheBemobiCheckout(checkoutPayload)
                        checkoutUrl = asText(bemobiCheckoutRef.current.checkoutUrl)
                    } catch (error) {
                        console.debug('Falha ao atualizar checkout Bemobi antes de copiar link', error)
                    }
                }
                if (checkoutUrl) {
                    const copied = await copyToClipboard(checkoutUrl)
                    appendMe(copied ? 'Link do checkout copiado.' : 'Nao foi possivel copiar automaticamente o link do checkout.')
                } else {
                    appendMe('Link do checkout indisponivel no momento.')
                }
                scrollToEnd()
                enqueueBotPlaceholder()
                const resp = await bot.enviarButton(action)
                handleResponse(resp)
                return
            }
            if (action === 'UPLOAD_DOC' || action === 'UPLOAD_SELFIE') {
                const senddoc = action === 'UPLOAD_DOC'
                const input = document.createElement('input')
                input.type = 'file'
                input.accept = `image/jpeg,image/png${senddoc ? ',application/pdf' : ''}`
                input.onchange = async () => {
                    if (!input.files?.length) {
                        return
                    }
                    const file = input.files[0]

                    if (senddoc && file.type === 'application/pdf') {
                        const pdfjsLib = await loadPdfJs()
                        const arrayBuffer = await file.arrayBuffer()
                        const pdf = await pdfjsLib.getDocument({data: arrayBuffer}).promise
                        const page = await pdf.getPage(1)

                        const viewport = page.getViewport({scale: 1.5})
                        const canvas = document.createElement('canvas')
                        const context = canvas.getContext('2d')
                        canvas.height = viewport.height
                        canvas.width = viewport.width

                        await page.render({canvasContext: context, viewport}).promise

                        messagesRef.current?.insertAdjacentHTML('beforeend', `<li me><img src="${canvas.toDataURL()}" style="max-width:150px;border-radius:6px" /></li>`)
                        scrollToEnd()
                    } else {
                        await new Promise((resolve) => {
                            const reader = new FileReader()
                            reader.onload = () => {
                                messagesRef.current?.insertAdjacentHTML('beforeend', `<li me><img src="${reader.result}" style="max-width:150px;border-radius:6px" /></li>`)
                                scrollToEnd()
                                resolve()
                            }
                            reader.onerror = () => resolve()
                            reader.readAsDataURL(file)
                        })
                    }

                    setFazendoUploadArquivo(true)
                    const form = new FormData()
                    form.append('file', file)
                    try {
                        enqueueBotPlaceholder()
                        const resp = action === 'UPLOAD_DOC' ? await bot.enviarDocumento(form) : await bot.enviarSelfie(form)
                        setFazendoUploadArquivo(false)
                        handleResponse(resp)
                    } catch (e) {
                        consumeBotPlaceholder()
                        setFazendoUploadArquivo(false)
                        console.error(e)
                    }
                }
                input.click()
                return
            }
            if (action === 'CAPTURAR_SELFIE') {
                setActionAtual('CAPTURAR_SELFIE')
                setShowWebcam(true)
                return false
            }

            if (action === 'CAPTURAR_FOTO') {
                setActionAtual('CAPTURAR_FOTO')
                setShowWebcam(true)
                return false
            }

            document.querySelectorAll("#chat #messages button").forEach(e => {
                e.setAttribute('disabled', true)
            })

            // default: BUTTONS
            appendMe(label)
            scrollToEnd()
            enqueueBotPlaceholder()
            const resp = await bot.enviarButton(action)
            handleResponse(resp)
        } catch (e) {
            consumeBotPlaceholder()
            console.error(e)
        }
    }

    function scrollToEnd() {
        const ul = messagesRef.current
        if (!ul) return
        ul.scrollTop = ul.scrollHeight
    }

    function scheduleScrollToEnd() {
        requestAnimationFrame(() => {
            scrollToEnd()
            setTimeout(scrollToEnd, 50)
        })
    }

    // Função para carregar PDF.js sob demanda
    const loadPdfJs = () => {
        return new Promise((resolve, reject) => {
            if (window.pdfjsLib) {
                resolve(window.pdfjsLib)
                return
            }

            const script = document.createElement('script')
            script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js'
            script.onload = () => {
                window.pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js'
                resolve(window.pdfjsLib)
            }
            script.onerror = reject
            document.head.appendChild(script)
        })
    }

    async function onCaptureSelfie(base64) {
        // Convert base64 to Blob then File
        const [meta, data] = base64.split(',')
        const mime = meta.match(/:(.*?);/)[1] || 'image/png'
        const bin = atob(data)
        const arr = new Uint8Array(bin.length)
        for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i)
        const file = new File([arr], 'selfie.png', {type: mime})
        const formData = new FormData()
        formData.append('file', file)

        document.querySelectorAll("#chat #messages button").forEach(e => {
            e.setAttribute('disabled', true)
        })

        setFazendoUploadArquivo(true)
        appendImage(base64)
        try {
            if (actionAtual === "CAPTURAR_SELFIE") {
                setShowWebcam(false)
                enqueueBotPlaceholder()
                const response = await bot.enviarSelfie(formData)
                handleResponse(response)
            }
            if (actionAtual === "CAPTURAR_FOTO") {
                setShowWebcam(false)
                console.log(bot)
                enqueueBotPlaceholder()
                const response = await bot.enviarDocumento(formData)
                handleResponse(response)
            }
        } catch (e) {
            consumeBotPlaceholder()
            console.error(e)
        } finally {
            setFazendoUploadArquivo(false)
        }

    }

    return (<>
        {showWebcam ? <ModalSelfie onClose={() => setShowWebcam(false)} onSend={onCaptureSelfie} action={actionAtual}/> : ''}

        <div id='chat' ref={chatRef}>
            <div
                className='header'
                style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 12, color: 'white'
                }}
            >
                <img
                    className='img-perfil'
                    src='/Ksk_Consorcio_files/Logo_full_white_ksk.svg'
                    alt='logo'
                />
                Canal de aquisição
                <div
                    className='header-actions'
                    style={{display: 'flex', gap: '.25rem'}}
                >
                    <button
                        id='reset-chat'
                        className='icon-btn'
                        title='Reiniciar conversa'
                        style={{
                            background: 'none', border: 'none', cursor: 'pointer', padding: '5px',
                        }}
                    >
                        <svg
                            xmlns='http://www.w3.org/2000/svg'
                            width='20'
                            height='20'
                            viewBox='0 0 24 24'
                            fill='none'
                            stroke='currentColor'
                            strokeWidth='2'
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            style={{color: '#fff'}}
                        >
                            <polyline points='23 4 23 10 17 10'></polyline>
                            <polyline points='1 20 1 14 7 14'></polyline>
                            <path d='M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15'></path>
                        </svg>
                    </button>
                    <button
                        id='close-chat'
                        style={{
                            background: 'none', border: 'none', cursor: 'pointer', padding: '5px',
                        }}
                    >
                        <svg
                            xmlns='http://www.w3.org/2000/svg'
                            width='20'
                            height='20'
                            viewBox='0 0 24 24'
                            fill='none'
                            stroke='currentColor'
                            strokeWidth='2'
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            style={{color: '#fff'}}
                        >
                            <line x1='18' y1='6' x2='6' y2='18'></line>
                            <line x1='6' y1='6' x2='18' y2='18'></line>
                        </svg>
                    </button>
                </div>
            </div>

            <div className='content'>
                <div className='d-flex justify-content-center' id='loading-chat'>
                    <div
                        className='spinner-border spinner-border mr-2'
                        style={{marginRight: 10, color: '#0B2145'}}
                        role='status'
                    ></div>
                </div>
                <ul id='messages' ref={messagesRef}></ul>
                <div id='send-message'>
                        <textarea className='text-color-gray text-bg-subarea' disabled={disableEnviar || fazendoUploadArquivo} ref={textareaRef}
                                  placeholder='Escreva algo...'></textarea>
                    <button
                        className='btnAction'
                        id='send-btn'
                        type='button'
                        disabled={fazendoUploadArquivo || disableEnviar || sending}
                        ref={sendBtnRef}
                    >
                        {(sending || fazendoUploadArquivo) ? 'Enviando' : 'Enviar'}
                        {fazendoUploadArquivo ? <div
                            className='spinner-border spinner-border-sm mr-2'
                            style={{marginLeft: 10, color: '#FFF'}}
                            role='status'
                        ></div> : ''}
                    </button>

                </div>
            </div>
        </div>
        {previewImage ? (
            <div
                className='image-lightbox'
                role='dialog'
                aria-modal='true'
                aria-label='Visualizacao da imagem'
                onClick={closePreviewImage}
            >
                <button
                    type='button'
                    className='image-lightbox__close'
                    aria-label='Fechar imagem'
                    onClick={(event) => {
                        event.stopPropagation()
                        closePreviewImage()
                    }}
                >
                    ×
                </button>
                <img
                    className='image-lightbox__img'
                    src={previewImage.src}
                    alt={previewImage.alt}
                    onClick={(event) => event.stopPropagation()}
                />
            </div>
        ) : ''}
    </>)
}
